/**
 * /forensic-enhancer — app: the shell, the stage and the five panels.
 *
 * engine.js owns pixels and state; this file owns the DOM. Rendering is one
 * function (render) fed by engine events, pointer work is one handler per
 * tool on one element (the stage viewport), and every control writes into
 * the engine and re-reads from it — no control carries state of its own.
 */
(function () {
'use strict';
if (!window.FX || window.FX.__app) return;
var FX = window.FX, S = FX.S, T = FX.T, CFG = FX.CFG;
FX.__app = true;

var $ = function (id) { return document.getElementById(id); };
var app = $('fxApp'); if (!app) return;
var startEl = $('fxStart'), editor = $('fxEditor'), decodeFail = $('fxDecodeFail');
var uploader = $('fxUpload'), video = $('fxVideo');
var stage = $('fxStage'), viewport = $('fxViewport'), layers = $('fxLayers');
var canvas = $('fxCanvas'), ctx = canvas.getContext('2d', { willReadFrequently: true });
var annoCanvas = $('fxAnnoCanvas'), annoCtx = annoCanvas.getContext('2d');
var analysisCanvas = $('fxAnalysisCanvas'), analysisCtx = analysisCanvas.getContext('2d');
var overlay = $('fxOverlay'), roiRect = $('fxRoiRect'), rulerSvg = $('fxRulerSvg'), perspSvg = $('fxPerspSvg'), perspPoly = $('fxPerspPoly'), perspPts = $('fxPerspPts');
var splitLine = $('fxSplitLine'), splitA = $('fxSplitA'), splitB = $('fxSplitB');
var cueEl = $('fxCue'), busyEl = $('fxBusy'), busyText = $('fxBusyText'), emptyEl = $('fxEmpty'), aiTag = $('fxAiTag');
var loupe = $('fxLoupe'), loupeCanvas = $('fxLoupeCanvas'), loupeCtx = loupeCanvas.getContext('2d');
var readout = $('fxReadout'), readSw = $('fxReadSw'), readPos = $('fxReadPos'), readRgb = $('fxReadRgb');
var toast = function (m, k) { try { window.__toast(m, k || 'info'); } catch (_) {} };
/* The tool's own events. Until 30.10.2026 they were a 'tool-used' DOM event nobody listened to:
   every track, read, OCR, preset and AI run since the rebuild reached no log at all. They go
   where the site's other tool events go — feature_use with an action. */
var stageEvt = function (name, data) { try { window.__ae && window.__ae('feature_use', Object.assign({ slug: CFG.slug, action: name }, data || {})); } catch (_) {} };
var fmtTime = function (s, frac) {
    if (!isFinite(s) || s < 0) s = 0;
    var m = Math.floor(s / 60), ss = s - m * 60;
    return m + ':' + (frac ? ss.toFixed(2).padStart(5, '0') : String(Math.floor(ss)).padStart(2, '0'));
};
/* Typed times go through the site's one time field (public/js/time-field.js): «1,20» is 1:20, not
   1 s; «1:02:03» is an hour, not 1:02; garbage is refused out loud instead of becoming 0. The
   fields stay the source of truth — after a commit their text is always the canonical value. */
var parseTime = function (str) {
    var v = window.TimeField ? window.TimeField.parse(str) : null;
    return v === null ? NaN : v;
};
function bindTimeInput(id, field) {
    var el = $(id);
    if (!el || !window.TimeField) return;
    var fmt = function (v) { return window.TimeField.format(v, { ms: 'auto' }); };
    // The text is the source (export.js writes it too), but a refused entry must fall
    // back to the last good value — not to whatever garbage is in the field right now.
    var last = window.TimeField.parse(el.value) || 0;
    window.TimeField.bind(el, {
        get: function () { var v = window.TimeField.parse(el.value); if (v !== null) last = v; return last; },
        set: function (v) { last = v; el.value = fmt(v); },
        min: 0, max: function () { return (S.video && S.video.duration) || Infinity; },
        format: fmt, slug: CFG.slug || 'forensic-enhancer', field: field,
        onInvalid: function (raw) { toast(String(T.time_unreadable || '').replace(':value', raw), 'error'); }
    });
}
(window.__timeFieldQ = window.__timeFieldQ || []).push(function () {
    ['fxExtractFrom', 'fxExtractTo', 'fxClipFrom', 'fxClipTo'].forEach(function (id) {
        bindTimeInput(id, id.replace(/^fx/, '').replace(/([A-Z])/g, function (c, x, i) { return (i ? '_' : '') + c.toLowerCase(); }));
    });
});
var fmtSize = function (b) { if (b < 1048576) return (b / 1024).toFixed(0) + ' KB'; if (b < 1073741824) return (b / 1048576).toFixed(1) + ' MB'; return (b / 1073741824).toFixed(2) + ' GB'; };
var stageTel = function (name) { try { window.__stage && window.__stage(name); } catch (_) {} };

/* ───────────────────────── UI state ───────────────────────── */
var UI = {
    tool: 'select',           // select | roi | ruler | persp | anno
    view: { z: 1, x: 0, y: 0, fitted: false },
    imgW: 0, imgH: 0,
    roi: null, roiStart: null,
    ruler: null, rulerDrag: null,
    persp: [], perspDrag: null,
    anno: { shape: 'arrow', color: '#ff3b30', items: [], redo: [], visible: true, start: null, preview: null },
    compare: false, split: 0.5, abHold: false, splitDrag: false,
    loupeOn: false, loupeK: 8, interp: 'nearest',
    analysis: null, analysisOpacity: 0.7,
    playing: false, scrubbing: false,
    sharpTrace: null,         // { t0, t1, scores[] , times[] }
    stackN: 8, stackMode: 'mean', stackAlign: true, sharpRange: 1,
    trackRange: 1, trackK: 3, trackUse: null,
    ocrMode: 'plate', aiScale: 2,
    pinch: null, pan: null,
};

/* ───────────────────────── stage: layout and render ───────────────────────── */
function setImageSize(w, h) {
    if (UI.imgW === w && UI.imgH === h) return;
    UI.imgW = w; UI.imgH = h;
    [canvas, annoCanvas, analysisCanvas].forEach(function (c) { c.width = w; c.height = h; });
    layers.style.width = w + 'px'; layers.style.height = h + 'px';
    overlay.style.width = w + 'px'; overlay.style.height = h + 'px';
    UI.view.fitted = false;
    fitView();
}
function fitView() {
    if (!UI.imgW) return;
    var vw = viewport.clientWidth - 16, vh = viewport.clientHeight - 16;
    if (vw < 10 || vh < 10) return;
    var z = Math.min(vw / UI.imgW, vh / UI.imgH, 4);
    UI.view.z = z; UI.view.x = (viewport.clientWidth - UI.imgW * z) / 2; UI.view.y = (viewport.clientHeight - UI.imgH * z) / 2; UI.view.fitted = true;
    applyView();
}
function applyView() {
    var v = UI.view;
    layers.style.transform = 'translate(' + v.x + 'px,' + v.y + 'px) scale(' + v.z + ')';
    layers.style.setProperty('--inv', String(1 / v.z));
    layers.classList.toggle('fx-zoomed', v.z > (fitZoom() + 0.001));
    $('fxZoomVal').textContent = Math.round(v.z * 100) + '%';
    drawRuler(); drawPersp(); drawRoi(); drawPlates();
}
function fitZoom() { var vw = viewport.clientWidth - 16, vh = viewport.clientHeight - 16; return UI.imgW ? Math.min(vw / UI.imgW, vh / UI.imgH, 4) : 1; }
function zoomAt(factor, cx, cy) {
    var v = UI.view, nz = Math.max(0.05, Math.min(32, v.z * factor));
    if (cx == null) { cx = viewport.clientWidth / 2; cy = viewport.clientHeight / 2; }
    v.x = cx - (cx - v.x) * (nz / v.z); v.y = cy - (cy - v.y) * (nz / v.z); v.z = nz; v.fitted = false;
    applyView();
}
function toImage(e) {
    var r = viewport.getBoundingClientRect();
    return { x: (e.clientX - r.left - UI.view.x) / UI.view.z, y: (e.clientY - r.top - UI.view.y) / UI.view.z };
}
function inImage(p) { return p.x >= 0 && p.y >= 0 && p.x < UI.imgW && p.y < UI.imgH; }

var renderQueued = false;
function render() {
    if (renderQueued) return; renderQueued = true;
    requestAnimationFrame(function () {
        renderQueued = false;
        var img = UI.abHold ? S.base : FX.composite();
        if (!img) { emptyEl.hidden = false; return; }
        emptyEl.hidden = true;
        setImageSize(img.width, img.height);
        ctx.putImageData(img, 0, 0);
        /* After a plate fusion "before" is ONE frame of the video, enlarged the
           ordinary way (bicubic) — the comparison that shows what the other
           frames added, not what the enhancement chain did. */
        var cw = S.sourceMeta.compareWith, useCw = !!(cw && !S.geom.actions.length && cw.width === img.width && cw.height === img.height);
        var left = useCw ? cw : S.base;
        if (UI.compare && left && !S.aiResult && left.width === img.width && left.height === img.height && !UI.abHold) {
            var sx = Math.round(img.width * UI.split);
            if (sx > 0) ctx.putImageData(left, 0, 0, 0, 0, sx, img.height);
            splitA.textContent = useCw ? T.track_before : T.compare_before;
            splitB.textContent = useCw ? T.track_after.replace(':n', S.sourceMeta.tracked ? S.sourceMeta.tracked.used : '') : T.compare_after;
            splitLine.hidden = false; splitLine.style.left = sx + 'px'; splitLine.style.setProperty('--inv', String(1 / UI.view.z));
            overlay.style.setProperty('--split-x', sx + 'px');
            splitA.hidden = false; splitB.hidden = false;
        } else { splitLine.hidden = true; splitA.hidden = true; splitB.hidden = true; }
        aiTag.hidden = !S.aiResult;
        $('fxAiRevert').hidden = !S.aiResult;
        drawAnno();
        if (UI.loupeOn && UI.lastPointer) drawLoupe(UI.lastPointer);
    });
}

/* ───────────────────────── cue, busy ───────────────────────── */
var cueTimer = null;
function cue(text, sticky) {
    clearTimeout(cueTimer);
    if (!text) { cueEl.classList.remove('is-on'); return; }
    cueEl.textContent = text; cueEl.classList.add('is-on');
    if (!sticky) cueTimer = setTimeout(function () { cueEl.classList.remove('is-on'); }, 2600);
}
FX.on('busy', function (b) { busyEl.classList.toggle('is-on', b.n > 0); if (b.text) busyText.textContent = b.text; });
FX.on('error', function (err) {
    var code = err && (err.code || err.message) || '';
    if (/OUT_OF_MEMORY|allocation/i.test(code)) toast(T.err_memory, 'error');
    else toast(T.err_worker, 'error');
    try { console.error(err); } catch (_) {}
});

/* An <svg> has no «hidden» property (it is HTMLElement's): «svg.hidden = false» set a JS field and
   left the attribute — the ruler line, the perspective points and the found plates were drawn into a
   layer that stayed display:none (the ruler since the 02.09 rebuild; the stand counted DOM nodes). */
function svgShow(el, on) { if (on) el.removeAttribute('hidden'); else el.setAttribute('hidden', ''); }

/* ───────────────────────── pointer tools ───────────────────────── */
function setTool(name) {
    UI.tool = name;
    layers.dataset.tool = name;
    document.querySelectorAll('[data-ptool]').forEach(function (b) { var on = b.dataset.ptool === name; b.classList.toggle('is-on', on); b.setAttribute('aria-pressed', on ? 'true' : 'false'); });
    var ctxEl = $('fxCtx');
    ['Roi', 'Ruler', 'Persp', 'Anno'].forEach(function (k) { $('fxCtx' + k).hidden = true; });
    var show = { roi: 'Roi', ruler: 'Ruler', persp: 'Persp', anno: 'Anno' }[name];
    ctxEl.hidden = !show;
    if (show) $('fxCtx' + show).hidden = false;
    if (name === 'roi') { $('fxCtxRoi').hidden = !UI.roi; ctxEl.hidden = !UI.roi; cue(T.cue_roi, true); }
    if (name === 'ruler') { $('fxRulerHint').textContent = UI.ruler ? '' : T.ruler_hint; cue(T.cue_ruler, true); }
    if (name === 'persp') { svgShow(perspSvg, true); drawPersp(); cue(T.cue_persp, true); }
    else svgShow(perspSvg, false);
    if (name === 'anno') cue(T.cue_anno, true);
    if (name === 'select') cue('');
    if (name !== 'ruler' && name !== 'select') { /* ruler stays drawn as a reference in select mode */ }
}
document.querySelectorAll('[data-ptool]').forEach(function (b) { b.addEventListener('click', function () { setTool(b.dataset.ptool); }); });
document.querySelectorAll('[data-ptool-go]').forEach(function (b) { b.addEventListener('click', function () { setTool(b.dataset.ptoolGo); }); });

/* ROI */
function drawRoi() {
    if (!UI.roi) { roiRect.hidden = true; return; }
    var r = UI.roi;
    roiRect.hidden = false;
    roiRect.style.left = r.x + 'px'; roiRect.style.top = r.y + 'px'; roiRect.style.width = r.w + 'px'; roiRect.style.height = r.h + 'px';
    roiRect.style.borderWidth = (1.5 / UI.view.z) + 'px';
    roiRect.dataset.size = Math.round(r.w) + ' × ' + Math.round(r.h) + ' px';
    $('fxRoiSize').textContent = Math.round(r.w) + ' × ' + Math.round(r.h);
}
function setRoi(r) {
    if (!r && UI.roiPlate) { UI.roiPlate = null; roiSeq++; drawPlates(); renderPlateCards(); }
    UI.roi = r;
    drawRoi();
    var has = !!(r && r.w > 4 && r.h > 4);
    if (UI.tool === 'roi') { $('fxCtx').hidden = !has; $('fxCtxRoi').hidden = !has; }
    $('fxStackRoiNote').textContent = has ? T.stack_roi_note : '';
    trackGauge();
}
$('fxRoiCrop').addEventListener('click', function () {
    if (!UI.roi || UI.roi.w < 4 || UI.roi.h < 4) { toast(T.roi_too_small, 'warning'); return; }
    var rect = { x: Math.max(0, Math.round(UI.roi.x)), y: Math.max(0, Math.round(UI.roi.y)), w: Math.round(UI.roi.w), h: Math.round(UI.roi.h) };
    setRoi(null);
    FX.addGeomAction({ type: 'crop', rect: rect });
    stageEvt('crop', { w: rect.w, h: rect.h });
    setTool('select');
});
$('fxRoiClear').addEventListener('click', function () { setRoi(null); });
$('fxRoiTrack').addEventListener('click', function () { runTrack(); });

/* Ruler */
function measureLabel(px) {
    var s = Math.round(px) + ' px';
    if (!S.rulerScale) return s;
    var real = px * S.rulerScale.perPx;
    var shown = real >= 100 ? real.toFixed(0) : real >= 10 ? real.toFixed(1) : real.toPrecision(3);
    return shown + ' ' + S.rulerScale.unit + ' · ' + s;
}
function drawRuler() {
    var m = UI.ruler;
    if (!m) { svgShow(rulerSvg, false); rulerSvg.innerHTML = ''; return; }
    svgShow(rulerSvg, true);
    rulerSvg.setAttribute('viewBox', '0 0 ' + UI.imgW + ' ' + UI.imgH);
    var inv = 1 / UI.view.z, d = Math.hypot(m.p2.x - m.p1.x, m.p2.y - m.p1.y);
    var mx = (m.p1.x + m.p2.x) / 2, my = (m.p1.y + m.p2.y) / 2;
    rulerSvg.innerHTML =
        '<line x1="' + m.p1.x + '" y1="' + m.p1.y + '" x2="' + m.p2.x + '" y2="' + m.p2.y + '" stroke="#ff3b30" stroke-width="' + (2 * inv) + '" stroke-dasharray="' + (6 * inv) + ' ' + (3 * inv) + '"/>' +
        '<circle class="fx-handle" data-ruler="p1" cx="' + m.p1.x + '" cy="' + m.p1.y + '" r="' + (9 * inv) + '" fill="#ff3b30" fill-opacity=".35" stroke="#fff" stroke-width="' + inv + '"/>' +
        '<circle class="fx-handle" data-ruler="p2" cx="' + m.p2.x + '" cy="' + m.p2.y + '" r="' + (9 * inv) + '" fill="#ff3b30" fill-opacity=".35" stroke="#fff" stroke-width="' + inv + '"/>' +
        '<g transform="translate(' + mx + ' ' + my + ') scale(' + inv + ')"><rect x="-70" y="-24" width="140" height="20" rx="5" fill="rgba(0,0,0,.72)"/><text x="0" y="-10" text-anchor="middle" fill="#fff" font-size="12" font-family="ui-monospace,monospace" font-weight="600">' + measureLabel(d) + '</text></g>';
    $('fxRulerCal').disabled = false;
}
$('fxRulerCal').addEventListener('click', function () {
    if (!UI.ruler) { toast(T.ruler_need_two, 'warning'); return; }
    var ref = parseFloat($('fxRulerRef').value);
    if (!(ref > 0)) { toast(T.ruler_need_value, 'warning'); return; }
    var px = Math.hypot(UI.ruler.p2.x - UI.ruler.p1.x, UI.ruler.p2.y - UI.ruler.p1.y); if (px < 1) return;
    var sel = $('fxRulerUnit');
    S.rulerScale = { perPx: ref / px, unit: sel.options[sel.selectedIndex].textContent.trim() || sel.value };
    $('fxRulerScale').textContent = S.rulerScale.perPx.toPrecision(3) + ' ' + S.rulerScale.unit + '/px';
    drawRuler(); toast(T.ruler_calibrated, 'success'); stageEvt('ruler_calibrated');
});
$('fxRulerClear').addEventListener('click', function () { UI.ruler = null; S.rulerScale = null; $('fxRulerScale').textContent = ''; $('fxRulerCal').disabled = true; drawRuler(); $('fxRulerHint').textContent = T.ruler_hint; });
FX.on('ruler_dropped', function () { $('fxRulerScale').textContent = ''; if (UI.ruler) { drawRuler(); toast(T.ruler_dropped, 'warning'); } });

/* Perspective */
function orderQuad(pts) {
    var cx = 0, cy = 0; pts.forEach(function (p) { cx += p.x / 4; cy += p.y / 4; });
    var sorted = pts.slice().sort(function (a, b) { return Math.atan2(a.y - cy, a.x - cx) - Math.atan2(b.y - cy, b.x - cx); });
    var start = 0, best = Infinity; sorted.forEach(function (p, i) { if (p.x + p.y < best) { best = p.x + p.y; start = i; } });
    return [sorted[start], sorted[(start + 1) % 4], sorted[(start + 2) % 4], sorted[(start + 3) % 4]];
}
function drawPersp() {
    perspPts.innerHTML = '';
    var inv = 1 / UI.view.z;
    UI.persp.forEach(function (p, i) {
        var d = document.createElement('div'); d.className = 'fx-persp-pt'; d.textContent = String(i + 1); d.dataset.idx = i;
        d.style.left = p.x + 'px'; d.style.top = p.y + 'px'; d.style.transform = 'scale(' + inv + ')';
        perspPts.appendChild(d);
    });
    perspSvg.setAttribute('viewBox', '0 0 ' + UI.imgW + ' ' + UI.imgH);
    perspPoly.setAttribute('points', UI.persp.length === 4 ? UI.persp.map(function (p) { return p.x + ',' + p.y; }).join(' ') : '');
    perspPoly.style.strokeWidth = (1.5 * inv) + 'px';
    var n = UI.persp.length;
    $('fxPerspApply').disabled = n !== 4;
    $('fxPerspHint').textContent = n === 4 ? T.persp_ready : T.persp_pick + ' (' + n + '/4)';
    $('fxPerspState').textContent = S.geom.actions.some(function (a) { return a.type === 'persp'; }) ? T.persp_done : '';
}
$('fxPerspStart').addEventListener('click', function () { setTool('persp'); switchTab('geom'); });
$('fxPerspClear').addEventListener('click', function () { UI.persp = []; drawPersp(); });
$('fxPerspApply').addEventListener('click', function () {
    if (UI.persp.length !== 4) return;
    var q = orderQuad(UI.persp);
    var dist = function (a, b) { return Math.hypot(a.x - b.x, a.y - b.y); };
    var outW = Math.round(Math.max(dist(q[0], q[1]), dist(q[3], q[2]))), outH = Math.round(Math.max(dist(q[0], q[3]), dist(q[1], q[2])));
    if (outW < 4 || outH < 4) { toast(T.persp_too_small, 'warning'); return; }
    if (outW * outH > 40e6) { toast(T.err_memory, 'error'); return; }
    UI.persp = []; drawPersp();
    FX.addGeomAction({ type: 'persp', quad: q, outW: outW, outH: outH }).then(function () { toast(T.persp_done, 'success'); stageEvt('perspective'); });
    setTool('select');
});

/* Annotations */
function drawArrow(c, x1, y1, x2, y2, col, lw) {
    c.strokeStyle = col; c.fillStyle = col; c.lineWidth = lw; c.beginPath(); c.moveTo(x1, y1); c.lineTo(x2, y2); c.stroke();
    var a = Math.atan2(y2 - y1, x2 - x1), hl = lw * 5;
    c.beginPath(); c.moveTo(x2, y2); c.lineTo(x2 - hl * Math.cos(a - Math.PI / 6), y2 - hl * Math.sin(a - Math.PI / 6)); c.lineTo(x2 - hl * Math.cos(a + Math.PI / 6), y2 - hl * Math.sin(a + Math.PI / 6)); c.closePath(); c.fill();
}
function drawAnnoItem(c, a, lw) {
    c.save(); c.strokeStyle = a.color; c.fillStyle = a.color; c.lineWidth = lw;
    if (a.type === 'arrow') drawArrow(c, a.x1, a.y1, a.x2, a.y2, a.color, lw);
    else if (a.type === 'rect') c.strokeRect(a.x, a.y, a.w, a.h);
    else if (a.type === 'circle') { c.beginPath(); c.ellipse(a.cx, a.cy, a.rx, a.ry, 0, 0, Math.PI * 2); c.stroke(); }
    else if (a.type === 'text') {
        var fs = Math.max(12, Math.round(lw * 7)); c.font = 'bold ' + fs + 'px system-ui, sans-serif';
        var m = c.measureText(a.text); c.fillStyle = 'rgba(0,0,0,.62)'; c.fillRect(a.x - 3, a.y - fs, m.width + 6, fs + 6); c.fillStyle = a.color; c.fillText(a.text, a.x, a.y);
    }
    c.restore();
}
function annoLineWidth() { return Math.max(1.5, Math.round(Math.max(UI.imgW, UI.imgH) / 500)); }
function drawAnno() {
    annoCtx.clearRect(0, 0, annoCanvas.width, annoCanvas.height);
    if (!UI.anno.visible) return;
    var lw = annoLineWidth();
    UI.anno.items.forEach(function (a) { drawAnnoItem(annoCtx, a, lw); });
    if (UI.anno.preview) { annoCtx.save(); annoCtx.setLineDash([6, 3]); drawAnnoItem(annoCtx, UI.anno.preview, lw); annoCtx.restore(); }
}
function buildAnno(shape, s, e) {
    if (shape === 'arrow') return { type: 'arrow', x1: s.x, y1: s.y, x2: e.x, y2: e.y, color: UI.anno.color };
    if (shape === 'rect') return { type: 'rect', x: Math.min(s.x, e.x), y: Math.min(s.y, e.y), w: Math.abs(e.x - s.x), h: Math.abs(e.y - s.y), color: UI.anno.color };
    if (shape === 'circle') return { type: 'circle', cx: (s.x + e.x) / 2, cy: (s.y + e.y) / 2, rx: Math.abs(e.x - s.x) / 2, ry: Math.abs(e.y - s.y) / 2, color: UI.anno.color };
    return null;
}
document.querySelectorAll('[data-anno]').forEach(function (b) { b.addEventListener('click', function () {
    document.querySelectorAll('[data-anno]').forEach(function (x) { x.classList.remove('is-active'); x.setAttribute('aria-checked', 'false'); });
    b.classList.add('is-active'); b.setAttribute('aria-checked', 'true'); UI.anno.shape = b.dataset.anno;
}); });
$('fxAnnoColor').addEventListener('input', function () { UI.anno.color = this.value; });
$('fxAnnoUndo').addEventListener('click', function () { if (UI.anno.items.length) { UI.anno.redo.push(UI.anno.items.pop()); drawAnno(); } });
$('fxAnnoClear').addEventListener('click', function () { UI.anno.items = []; UI.anno.redo = []; drawAnno(); });
$('fxAnnoToggle').addEventListener('click', function () { UI.anno.visible = !UI.anno.visible; this.setAttribute('aria-pressed', UI.anno.visible ? 'true' : 'false'); drawAnno(); });
document.querySelectorAll('[data-anno-tpl]').forEach(function (b) { b.addEventListener('click', function () {
    if (!UI.imgW) return;
    var w = UI.imgW, h = UI.imgH, lw = annoLineWidth();
    if (b.dataset.annoTpl === 'plate') {
        var r = UI.roi && UI.roi.w > 4 ? UI.roi : { x: w * 0.35, y: h * 0.4, w: w * 0.3, h: h * 0.2 };
        UI.anno.items.push({ type: 'rect', x: r.x, y: r.y, w: r.w, h: r.h, color: UI.anno.color });
        UI.anno.items.push({ type: 'text', x: r.x, y: Math.max(lw * 8, r.y - lw * 2), text: T.anno_tpl_plate_text, color: UI.anno.color });
    } else {
        var ts = new Date().toISOString().replace('T', ' ').slice(0, 19);
        if (S.kind === 'video') ts = fmtTime(S.sourceMeta.time, true) + ' · #' + S.sourceMeta.frame + ' · ' + ts;
        UI.anno.items.push({ type: 'text', x: lw * 4, y: h - lw * 4, text: ts, color: '#34c759' });
    }
    UI.anno.redo = []; drawAnno();
}); });

/* One pointer handler for the whole stage. */
var activePointers = new Map();
viewport.addEventListener('pointerdown', function (e) {
    if (e.button !== 0 && e.pointerType === 'mouse' && e.button !== 1) return;
    activePointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (activePointers.size === 2) { // pinch
        var pts = Array.from(activePointers.values());
        UI.pinch = { d: Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y), z: UI.view.z, cx: (pts[0].x + pts[1].x) / 2, cy: (pts[0].y + pts[1].y) / 2 };
        UI.pan = null; UI.roiStart = null; return;
    }
    viewport.setPointerCapture(e.pointerId);
    if (!UI.imgW) return;
    var p = toImage(e);
    if (e.target.closest('.fx-split-line') || (UI.compare && Math.abs(p.x - UI.imgW * UI.split) * UI.view.z < 10)) { UI.splitDrag = true; return; }
    var handle = e.target.closest('[data-ruler]');
    if (handle) { UI.rulerDrag = handle.dataset.ruler; return; }
    var ppt = e.target.closest('.fx-persp-pt');
    if (ppt) { UI.perspDrag = parseInt(ppt.dataset.idx, 10); return; }
    var pl = UI.tool === 'select' && e.target.closest('[data-plate]');
    if (pl && pl.dataset.plate !== 'roi') { selectPlate(parseInt(pl.dataset.plate, 10)); stageEvt('plate_pick', { how: 'stage' }); return; }
    var pan = e.button === 1 || UI.spaceHeld || (UI.tool === 'select' && layers.classList.contains('fx-zoomed'));
    if (pan) { UI.pan = { x: e.clientX - UI.view.x, y: e.clientY - UI.view.y }; layers.classList.add('is-panning'); return; }
    if (!inImage(p)) return;
    if (UI.tool === 'roi') { UI.roiStart = p; setRoi({ x: p.x, y: p.y, w: 0, h: 0 }); }
    else if (UI.tool === 'ruler') {
        if (!UI.rulerTmp) { UI.rulerTmp = p; UI.ruler = { p1: p, p2: p }; UI.rulerDrag = 'p2'; drawRuler(); }
    }
    else if (UI.tool === 'persp') { if (UI.persp.length < 4) { UI.persp.push(p); drawPersp(); } }
    else if (UI.tool === 'anno') {
        if (UI.anno.shape === 'text') {
            var text = prompt(T.anno_text_prompt); if (text) { UI.anno.items.push({ type: 'text', x: p.x, y: p.y, text: text, color: UI.anno.color }); UI.anno.redo = []; drawAnno(); }
        } else UI.anno.start = p;
    }
});
viewport.addEventListener('pointermove', function (e) {
    if (activePointers.has(e.pointerId)) activePointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (UI.pinch && activePointers.size === 2) {
        var pts = Array.from(activePointers.values());
        var d = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);
        var r = viewport.getBoundingClientRect();
        var nz = Math.max(0.05, Math.min(32, UI.pinch.z * d / UI.pinch.d));
        var cx = UI.pinch.cx - r.left, cy = UI.pinch.cy - r.top;
        UI.view.x = cx - (cx - UI.view.x) * (nz / UI.view.z); UI.view.y = cy - (cy - UI.view.y) * (nz / UI.view.z); UI.view.z = nz; UI.view.fitted = false; applyView();
        return;
    }
    if (!UI.imgW) return;
    var p = toImage(e);
    UI.lastPointer = { clientX: e.clientX, clientY: e.clientY, img: p };
    if (UI.pan) { UI.view.x = e.clientX - UI.pan.x; UI.view.y = e.clientY - UI.pan.y; UI.view.fitted = false; applyView(); return; }
    if (UI.splitDrag) { UI.split = Math.max(0.02, Math.min(0.98, p.x / UI.imgW)); render(); return; }
    if (UI.rulerDrag && UI.ruler) { UI.ruler[UI.rulerDrag] = clampPt(p); drawRuler(); return; }
    if (UI.perspDrag != null) { UI.persp[UI.perspDrag] = clampPt(p); drawPersp(); return; }
    if (UI.roiStart) { var q = clampPt(p); setRoi({ x: Math.min(UI.roiStart.x, q.x), y: Math.min(UI.roiStart.y, q.y), w: Math.abs(q.x - UI.roiStart.x), h: Math.abs(q.y - UI.roiStart.y) }); return; }
    if (UI.anno.start) { UI.anno.preview = buildAnno(UI.anno.shape, UI.anno.start, clampPt(p)); drawAnno(); return; }
    updateReadout(p);
    if (UI.loupeOn) drawLoupe(UI.lastPointer);
});
function endPointer(e) {
    activePointers.delete(e.pointerId);
    if (activePointers.size < 2) UI.pinch = null;
    if (UI.pan) { UI.pan = null; layers.classList.remove('is-panning'); }
    UI.splitDrag = false;
    if (UI.rulerDrag) { UI.rulerDrag = null; UI.rulerTmp = null; if (UI.ruler && Math.hypot(UI.ruler.p2.x - UI.ruler.p1.x, UI.ruler.p2.y - UI.ruler.p1.y) < 3) { UI.ruler = null; drawRuler(); $('fxRulerCal').disabled = true; } else $('fxRulerHint').textContent = ''; }
    UI.perspDrag = null;
    if (UI.roiStart) { UI.roiStart = null; if (UI.roi && (UI.roi.w < 4 || UI.roi.h < 4)) setRoi(null); else if (UI.roi) onRoiDrawn(UI.roi); }
    if (UI.anno.start) {
        var p = clampPt(toImage(e));
        var a = buildAnno(UI.anno.shape, UI.anno.start, p);
        UI.anno.start = null; UI.anno.preview = null;
        if (a && ((a.type === 'arrow' && Math.hypot(a.x2 - a.x1, a.y2 - a.y1) > 3) || (a.type === 'rect' && a.w > 3 && a.h > 3) || (a.type === 'circle' && a.rx > 2 && a.ry > 2))) { UI.anno.items.push(a); UI.anno.redo = []; }
        drawAnno();
    }
}
viewport.addEventListener('pointerup', endPointer);
viewport.addEventListener('pointercancel', endPointer);
viewport.addEventListener('pointerleave', function () { readout.hidden = true; if (UI.loupeOn) loupe.classList.remove('is-on'); });
viewport.addEventListener('wheel', function (e) {
    if (!UI.imgW) return; e.preventDefault();
    var r = viewport.getBoundingClientRect();
    zoomAt(e.deltaY > 0 ? 0.9 : 1.1, e.clientX - r.left, e.clientY - r.top);
}, { passive: false });
viewport.addEventListener('dblclick', function () { if (UI.tool === 'select') fitView(); });
function clampPt(p) { return { x: Math.max(0, Math.min(UI.imgW, p.x)), y: Math.max(0, Math.min(UI.imgH, p.y)) }; }

/* Readout + loupe */
function updateReadout(p) {
    var img = FX.composite();
    if (!img || !inImage(p)) { readout.hidden = true; return; }
    var x = p.x | 0, y = p.y | 0, i = (y * img.width + x) * 4, d = img.data;
    readout.hidden = false;
    readSw.style.background = 'rgb(' + d[i] + ',' + d[i + 1] + ',' + d[i + 2] + ')';
    readPos.textContent = x + ', ' + y;
    readRgb.textContent = d[i] + ' ' + d[i + 1] + ' ' + d[i + 2];
}
function drawLoupe(ptr) {
    if (!ptr || !UI.imgW || !inImage(ptr.img)) { loupe.classList.remove('is-on'); return; }
    loupe.classList.add('is-on');
    loupe.style.left = (ptr.clientX - 100) + 'px'; loupe.style.top = (ptr.clientY - 100) + 'px';
    var k = UI.loupeK, src = 200 / k;
    loupeCtx.imageSmoothingEnabled = false;
    loupeCtx.clearRect(0, 0, 200, 200);
    loupeCtx.save(); loupeCtx.beginPath(); loupeCtx.arc(100, 100, 100, 0, Math.PI * 2); loupeCtx.clip();
    loupeCtx.drawImage(canvas, ptr.img.x - src / 2, ptr.img.y - src / 2, src, src, 0, 0, 200, 200);
    if (UI.anno.visible) loupeCtx.drawImage(annoCanvas, ptr.img.x - src / 2, ptr.img.y - src / 2, src, src, 0, 0, 200, 200);
    loupeCtx.strokeStyle = 'rgba(255,255,255,.55)'; loupeCtx.lineWidth = 1; loupeCtx.beginPath(); loupeCtx.moveTo(100, 0); loupeCtx.lineTo(100, 200); loupeCtx.moveTo(0, 100); loupeCtx.lineTo(200, 100); loupeCtx.stroke();
    loupeCtx.restore();
}
$('fxLoupeBtn').addEventListener('click', function () {
    UI.loupeOn = !UI.loupeOn; this.setAttribute('aria-pressed', UI.loupeOn ? 'true' : 'false');
    if (!UI.loupeOn) loupe.classList.remove('is-on'); else if (UI.lastPointer) drawLoupe(UI.lastPointer);
});
document.querySelectorAll('[data-loupe]').forEach(function (b) { b.addEventListener('click', function () { seg(b); UI.loupeK = parseInt(b.dataset.loupe, 10); if (UI.loupeOn && UI.lastPointer) drawLoupe(UI.lastPointer); }); });
document.querySelectorAll('[data-interp]').forEach(function (b) { b.addEventListener('click', function () { seg(b); UI.interp = b.dataset.interp; layers.classList.toggle('fx-nearest', UI.interp === 'nearest'); }); });
layers.classList.add('fx-nearest');

/* Zoom pill */
$('fxZoomIn').addEventListener('click', function () { zoomAt(1.25); });
$('fxZoomOut').addEventListener('click', function () { zoomAt(0.8); });
$('fxZoomFit').addEventListener('click', fitView);
$('fxZoom1').addEventListener('click', function () { var v = UI.view; var cx = viewport.clientWidth / 2, cy = viewport.clientHeight / 2; zoomAt(1 / v.z, cx, cy); });
/* Safari до 13.1 наблюдателя не знает: голый конструктор здесь обрывал весь
   файл ниже — сравнение, полный экран, вкладки. Без него вид следует за окном
   (вьюпорт живёт в ширину страницы; полный экран перестраивает сам). */
function refitView() { if (UI.view.fitted) fitView(); else applyView(); }
if (typeof ResizeObserver === 'function') new ResizeObserver(refitView).observe(viewport);
else window.addEventListener('resize', refitView);

/* Compare */
$('fxCompare').addEventListener('click', function () { UI.compare = !UI.compare; this.setAttribute('aria-pressed', UI.compare ? 'true' : 'false'); if (UI.compare) cue(T.cue_compare); render(); });

/* Fullscreen */
$('fxFullscreen').addEventListener('click', function () {
    if (document.fullscreenElement) { document.exitFullscreen && document.exitFullscreen(); return; }
    if (editor.requestFullscreen) editor.requestFullscreen().catch(function () {});
});
document.addEventListener('fullscreenchange', function () { setTimeout(fitView, 60); });

/* ───────────────────────── tabs ───────────────────────── */
function switchTab(name) {
    document.querySelectorAll('.fx-tab').forEach(function (b) { var on = b.dataset.tab === name; b.classList.toggle('is-active', on); b.setAttribute('aria-selected', on ? 'true' : 'false'); });
    document.querySelectorAll('.fx-panel').forEach(function (p) { p.classList.toggle('is-active', p.dataset.panel === name); });
    try { window.__toolSettings && window.__toolSettings.save(CFG.slug, 'tab', name); } catch (_) {}
}
document.querySelectorAll('.fx-tab').forEach(function (b) { b.addEventListener('click', function () { switchTab(b.dataset.tab); }); });
function seg(btn) {
    var group = btn.parentElement;
    group.querySelectorAll('.fx-seg-b').forEach(function (x) { x.classList.remove('is-active'); x.setAttribute('aria-checked', 'false'); });
    btn.classList.add('is-active'); btn.setAttribute('aria-checked', 'true');
}

/* ───────────────────────── panel 1 · frame ───────────────────────── */
document.querySelectorAll('[data-sharp-range]').forEach(function (b) { b.addEventListener('click', function () { seg(b); UI.sharpRange = parseFloat(b.dataset.sharpRange); }); });
$('fxSharpGo').addEventListener('click', function () {
    if (!S.video.loaded) return;
    var btn = this; btn.disabled = true;
    var prog = $('fxSharpProg'); prog.hidden = false; prog.firstElementChild.style.width = '0%';
    var box = UI.roi && UI.roi.w > 8 ? roiToSource(UI.roi) : null;
    var t0 = performance.now();
    FX.findSharpest(UI.sharpRange, box).then(function (ranked) {
        prog.hidden = true; btn.disabled = false;
        var list = $('fxSharpList'); list.innerHTML = '';
        if (!ranked.length) { toast(T.sharp_none, 'warning'); return; }
        UI.sharpTrace = ranked.slice().sort(function (a, b) { return a.time - b.time; });
        drawScrubTrace();
        var top = ranked.slice(0, 5), max = ranked[0].score || 1;
        top.forEach(function (r, i) {
            var it = document.createElement('button'); it.type = 'button'; it.className = 'fx-list-item' + (i === 0 ? ' is-best' : '');
            it.innerHTML = '<b class="fx-mono">' + fmtTime(r.time, true) + '</b><small>#' + r.frame + '</small><span class="fx-spacer"></span><small>' + (i === 0 ? '★ ' + T.sharp_best + ' · ' : '') + T.sharp_score + ' ' + Math.round(r.score / max * 100) + '</small>';
            // the MIDDLE of the ranked frame: its start time sits on a frame boundary
            it.addEventListener('click', function () { goToTime(FX.timeOfFrame(r.frame), { best: i === 0 }); });
            list.appendChild(it);
        });
        toast(T.sharp_done, 'success');
        stageEvt('sharpest', { n: ranked.length, ms: Math.round(performance.now() - t0), roi: !!box });
        goToTime(FX.timeOfFrame(ranked[0].frame), { best: true });
    }).catch(function (e) { prog.hidden = true; btn.disabled = false; toast(T.err_worker, 'error'); try { console.error(e); } catch (_) {} });
});
FX.on('progress', function (p) {
    var el = { sharp: 'fxSharpProg', stack: 'fxStackProg', deblur: null }[p.key];
    if (el && $(el)) $(el).firstElementChild.style.width = Math.round(p.p * 100) + '%';
});
/* A box drawn on the current (geometry-applied) picture, expressed in SOURCE
   frame coordinates. Exact when the only action is a crop; otherwise the
   whole frame is used, which is what the callers do with null. */
function roiToSource(r) {
    var g = S.geom;
    if (g.rotate || g.par !== 1 || g.k1) return null;
    var acts = g.actions;
    if (!acts.length) return { x: r.x, y: r.y, w: r.w, h: r.h };
    if (acts.length === 1 && acts[0].type === 'crop') { var c = acts[0].rect; return { x: c.x + r.x, y: c.y + r.y, w: r.w, h: r.h }; }
    return null;
}

document.querySelectorAll('[data-stack-n]').forEach(function (b) { b.addEventListener('click', function () { seg(b); UI.stackN = parseInt(b.dataset.stackN, 10); }); });
document.querySelectorAll('[data-stack-mode]').forEach(function (b) { b.addEventListener('click', function () { seg(b); UI.stackMode = b.dataset.stackMode; }); });
$('fxStackAlign').addEventListener('click', function () { UI.stackAlign = !UI.stackAlign; this.setAttribute('aria-checked', UI.stackAlign ? 'true' : 'false'); });
$('fxStackGo').addEventListener('click', function () {
    if (!S.video.loaded) { toast(T.stack_need_video, 'warning'); return; }
    var bill = FX.bill(); if (!bill) return;
    var btn = this; btn.disabled = true;
    var prog = $('fxStackProg'); prog.hidden = false; prog.firstElementChild.style.width = '0%';
    var t0 = performance.now();
    FX.stackAround(UI.stackN, UI.stackMode, UI.stackAlign).then(function (info) {
        bill.commit();
        resultDelivered('stack', { n: info.n });
        prog.hidden = true; btn.disabled = false;
        var msg = T.stack_done.replace(':n', info.n) + (info.aligned && info.maxShift > 0 ? ' · ' + T.stack_shift.replace(':px', info.maxShift) : '');
        $('fxStackInfo').textContent = msg;
        toast(msg, 'success');
        if (info.aligned && info.maxShift >= 12 && !info.roi) toast(T.stack_moving, 'warning');
        stageEvt('stack', { n: info.n, mode: info.mode, shift: info.maxShift, roi: info.roi, ms: Math.round(performance.now() - t0) });
        updateFileMeta();
    }).catch(function (e) {
        bill.abort();
        prog.hidden = true; btn.disabled = false;
        /* Three unrelated causes used to share one sentence. A forensic tool
           whose whole promise is "read what is on that frame" cannot answer
           "it failed" and leave the person guessing which knob to turn. */
        var msg = T.stack_failed;
        if (e && e.code === 'SIZE_MISMATCH') msg = T.stack_mismatch;
        else if (e && e.code === 'TOO_FEW') msg = (T.stack_too_few || T.stack_failed)
            .replace(':got', e.got == null ? '0' : e.got).replace(':asked', e.asked == null ? '' : e.asked);
        toast(msg, 'error');
        try { window.__ae('tool_error', { slug: 'forensic-enhancer', error_type: 'stack', code: (e && e.code) || 'unknown', got: e && e.got, asked: e && e.asked }); } catch (_) {}
        try { console.error(e); } catch (_) {}
    });
});

/* ── Plate in motion: follow the box through the neighbouring frames and fuse ── */
/* A result left on the screen is a delivered result in this tool (a plate read
   off the screen never needs a download): it is completed for telemetry here,
   once per run, beside the export menu's own completions. */
function resultDelivered(kind, extra) {
    try { window.__aeToolComplete && window.__aeToolComplete(Object.assign({ kind: kind, file_type: S.kind }, extra || {})); } catch (_) {}
    try { window.__stage && window.__stage('result'); } catch (_) {}
}
/* What a box this wide can give, said before the work and not after it
   (thresholds from the plate-reading literature: under ~30 px of plate width
   no method reads characters reliably once a video is compressed; 30–60 px is
   the band where frame fusion decides; from ~60 px a good frame usually does). */
function trackGauge() {
    var el = $('fxTrackGauge'); if (!el) return;
    var r = UI.roi && UI.roi.w > 4 && UI.roi.h > 4 ? UI.roi : null;
    if (!r || S.kind !== 'video' || S.sourceMeta.tracked) { el.hidden = true; return; }
    var w = Math.round(r.w), h = Math.round(r.h);
    var msg = w < 34 ? T.track_gauge_small : w < 70 ? T.track_gauge_mid : T.track_gauge_ok;
    el.hidden = false;
    el.textContent = T.track_gauge.replace(':w', w).replace(':h', h) + ' ' + msg;
}
document.querySelectorAll('[data-track-range]').forEach(function (b) { b.addEventListener('click', function () { seg(b); UI.trackRange = parseFloat(b.dataset.trackRange); }); });
document.querySelectorAll('[data-track-k]').forEach(function (b) { b.addEventListener('click', function () {
    seg(b); UI.trackK = parseInt(b.dataset.trackK, 10);
    // the window is still in the worker: a different scale is a re-fuse, not a re-run
    if (S.sourceMeta.tracked && S.track) refuse();
}); });
$('fxTrackGo').addEventListener('click', function () { runTrack(); });
/* What is followed: the PLATE — found on the frame, picked, or found inside the drawn box — as
   its own box with a little bumper round it; only when no plate is known, the drawn box itself
   (a face, a sign). A box round the car is a template of a 3-D thing that turns: the tracker's
   homography cannot hold it, and that was «followed in 1 of 60 frames» in the telemetry. */
function runTrack(opts) {
    opts = opts || {};
    if (!S.video.loaded) { toast(T.stack_need_video, 'warning'); return; }
    if (S.sourceMeta.tracked) { toast(T.track_on_fused, 'warning'); return; }
    var plate = opts.plate || activePlate();
    if (plate && plate.boxOnly) plate = null;               // no plate seen in the box: follow the box as drawn
    var raw = plate ? PL.trackBox(plate, UI.imgW, UI.imgH) : (UI.roi && UI.roi.w >= 8 && UI.roi.h >= 6 ? UI.roi : null);
    var src = plate ? (plate === UI.roiPlate ? 'roi_plate' : 'plate') : 'box';
    var box = raw ? roiToSource(raw) : null;
    if (!box) {
        if (raw) { toast(T.track_geom, 'warning'); return; }
        switchTab('frame'); setTool('roi'); cue(T.track_need_box, true);
        // on a phone the panel is BELOW the picture: bring the picture into view
        // to draw on — 'nearest' moves nothing when it is already on screen
        try { stage.scrollIntoView({ block: 'nearest' }); } catch (_) {}
        return;
    }
    box = { x: Math.round(box.x), y: Math.round(box.y), w: Math.round(box.w), h: Math.round(box.h) };
    var bill = FX.bill(); if (!bill) return;
    var btns = [$('fxTrackGo'), $('fxRoiTrack')].concat(Array.from(document.querySelectorAll('.fx-pcard .fx-btn')));
    btns.forEach(function (b) { b.disabled = true; });
    var prog = $('fxTrackProg'); prog.hidden = false; prog.firstElementChild.style.width = '0%';
    if (opts.thenRead) platesState(T.plate_fusing);
    var t0 = performance.now();
    FX.trackFuse(box, { range: UI.trackRange, K: UI.trackK }).then(function (info) {
        bill.commit();
        prog.hidden = true; btns.forEach(function (b) { b.disabled = false; });
        setRoi(null); setTool('select');
        /* The fused picture is still soft — the lens, the codec and the fusion
           kernel all blur it — and its noise is down by √N, which is exactly
           when a deconvolution helps instead of hurting. On, in the chain,
           where it can be seen and switched off. σ = K/2, measured on a
           synthetic H.264 plate (44–64 px wide, crf 36, ×3): correlation with
           the true plate 0.881 → 0.906 at σ 1.5; σ 2.1 rang (0.888); on ONE
           frame every σ made it worse (0.855 → 0.832–0.847). */
        FX.setStep('optical', { on: true, sigma: Math.round(0.5 * info.K * 10) / 10, iters: 10 }, { now: true });
        showTrackResult(info);
        UI.compare = true; UI.split = 0.5; $('fxCompare').setAttribute('aria-pressed', 'true');
        cue(T.track_cue_compare.replace(':n', info.used));
        render(); updateFileMeta();
        var ms = Math.round(performance.now() - t0);
        stageEvt('track', { used: info.used, n: info.n, tracked: info.tracked, k: info.K, rho: info.rho, bw: box.w, bh: box.h, src: src, range: UI.trackRange, model: info.model, smin: info.scaleMin, smax: info.scaleMax, ms: ms, ms_dec: info.msDecode, ms_trk: info.msTrack, ms_fuse: info.msFuse });
        resultDelivered('track', { used: info.used, n: info.n, k: info.K, bw: box.w, ms: ms, src: src });
        // «assemble and read» is one press: the fused plate goes straight to the reader
        if (opts.thenRead) readPlate('fuse');
    }).catch(function (e) {
        bill.abort();
        prog.hidden = true; btns.forEach(function (b) { b.disabled = false; });
        var code = (e && e.code) || '';
        /* A box over most of the frame is not a flat thing the tracker can hold (the road, the car, the
           camera's own motion), and its window shrinks to a few frames by memory: the telemetry's
           «1 of 10» on 2270×1285 boxes. Say what to box instead. */
        var huge = !plate && code === 'TOO_FEW' && box.w * box.h > 0.15 * (S.video.w * S.video.h || Infinity);
        var msg = huge ? T.track_box_huge
            : code === 'TOO_FEW' ? T.track_too_few.replace(':got', e.got == null ? '0' : e.got).replace(':n', e.n == null ? '' : e.n)
            : code === 'BOX_TOO_SMALL' ? T.roi_too_small
            : /OUT_OF_MEMORY|allocation/i.test(code + ' ' + (e && e.message)) ? T.err_memory
            // the worker never started (its script did not arrive) or died: not the box's fault
            : /WORKER_CRASHED/.test(code + ' ' + (e && e.message)) ? T.err_crash
            : T.track_failed;
        if (opts.thenRead) platesState('');
        /* «Assemble and read» asked for a plate: when the frames cannot be assembled the person
           still gets one — read on this frame, said as such (the toast says why it is one frame). */
        if (opts.thenRead && plate && code !== 'OUT_OF_MEMORY') {
            toast(T.track_fallback_single, 'warning');
            readPlate('fuse_fallback', plate);
        } else toast(msg, 'error');
        try { window.__ae && window.__ae('tool_error', { slug: CFG.slug, error_type: 'track', code: code || String(e && e.message || 'unknown').slice(0, 60), got: e && e.got, n: e && e.n, bw: box.w, bh: box.h, src: src,
            diag: e && e.diag ? String(e.diag).slice(0, 160) : undefined, lane: S.video.lastLane, fd: S.video.lastDecodeDims, vd: S.video.w + 'x' + S.video.h, codec: S.video.codec || '' }); } catch (_) {}
        try { console.error(e); } catch (_) {}
    });
}
function refuse() {
    var use = UI.trackUse ? Array.from(UI.trackUse) : null;
    $('fxTrackRefuse').disabled = true;
    FX.trackRefuse(use, UI.trackK).then(function (info) {
        showTrackResult(info); render(); updateFileMeta();
        stageEvt('track_refuse', { used: info.used, k: info.K });
    }).catch(function (e) {
        var code = (e && e.code) || '';
        toast(code === 'TOO_FEW' ? T.track_too_few.replace(':got', e.got == null ? '0' : e.got).replace(':n', e.n == null ? '' : e.n) : T.track_failed, 'error');
        $('fxTrackRefuse').disabled = false;
    });
}
$('fxTrackRefuse').addEventListener('click', refuse);
$('fxTrackBack').addEventListener('click', function () {
    var t = S.sourceMeta.tracked ? S.sourceMeta.tracked.time : video.currentTime;
    goToTime(t);
});
$('fxTrackPreset').addEventListener('click', function () {
    // the plate preset on top of the fusion, keeping the deconvolution it got
    var keep = JSON.parse(JSON.stringify(S.enh.steps.optical));
    FX.applyPreset('plate');
    S.enh.steps.denoise.on = false;     // the frames already averaged the noise away
    S.enh.steps.optical = keep;
    FX.emit('enh'); FX.scheduleEnhance(0);
    document.querySelectorAll('[data-preset]').forEach(function (x) { x.classList.toggle('is-active', x.dataset.preset === 'plate'); });
    toast(T.preset_applied, 'success');
    stageEvt('track_preset');
});
function hideTrackResult() { $('fxTrackResult').hidden = true; $('fxTrackStrip').innerHTML = ''; UI.trackUse = null; }
FX.on('track_end', hideTrackResult);
function showTrackResult(info) {
    $('fxTrackResult').hidden = false;
    $('fxTrackInfo').textContent = T.track_info
        .replace(':used', info.used).replace(':n', info.n).replace(':k', info.K)
        .replace(':from', fmtTime(info.t0, true)).replace(':to', fmtTime(info.t1, true))
        .replace(':rho', (info.rho || 0).toFixed(2));
    var host = $('fxTrackStrip'); host.innerHTML = '';
    UI.trackUse = new Set();
    (info.frames || []).forEach(function (f) {
        if (f.used) UI.trackUse.add(f.i);
        if (!f.thumb) return;
        var b = document.createElement('button'); b.type = 'button';
        b.className = 'fx-tthumb' + (f.used ? ' is-used' : '') + (f.ref ? ' is-ref' : '') + (f.ok ? '' : ' is-weak');
        b.title = fmtTime(f.ts, true) + ' · ρ ' + f.rho.toFixed(2) + (f.ref ? ' · ★' : '');
        b.setAttribute('aria-pressed', f.used ? 'true' : 'false');
        var c = document.createElement('canvas'); c.width = f.tw; c.height = f.th;
        var id = c.getContext('2d').createImageData(f.tw, f.th), d = id.data, t = f.thumb;
        for (var i = 0, j = 0; i < t.length; i++, j += 4) { d[j] = d[j + 1] = d[j + 2] = t[i]; d[j + 3] = 255; }
        c.getContext('2d').putImageData(id, 0, 0);
        b.appendChild(c);
        if (f.ok && !f.ref) b.addEventListener('click', function () {
            var on = !UI.trackUse.has(f.i);
            if (on) UI.trackUse.add(f.i); else UI.trackUse.delete(f.i);
            b.classList.toggle('is-used', on); b.setAttribute('aria-pressed', on ? 'true' : 'false');
            $('fxTrackRefuse').disabled = false;
        });
        else b.disabled = !f.ref;
        host.appendChild(b);
    });
    $('fxTrackRefuse').disabled = true;
}

$('fxCapture').addEventListener('click', function () {
    if (!S.source) return;
    FX.addCaptured(S.source, S.sourceMeta.time, S.sourceMeta.frame).then(function () { toast(T.capture_added, 'success'); });
});
$('fxExtractToggle').addEventListener('click', function () { var row = $('fxExtractRow'); row.hidden = !row.hidden; this.setAttribute('aria-expanded', row.hidden ? 'false' : 'true'); });
var extractAbort = null;
$('fxExtractGo').addEventListener('click', function () {
    if (!S.video.loaded || extractAbort) return;
    var fps = Math.max(1, Math.min(60, parseInt($('fxExtractFps').value, 10) || 2));
    var from = parseTime($('fxExtractFrom').value), to = parseTime($('fxExtractTo').value);
    if (!isFinite(from)) from = 0;
    if (!isFinite(to) || to <= from) to = S.video.duration;
    var times = []; for (var t = from; t <= to && times.length < 600; t += 1 / fps) times.push(t);
    var stop = false; extractAbort = function () { stop = true; };
    $('fxExtractGo').hidden = true; $('fxExtractStop').hidden = false;
    var prog = $('fxExtractProg'); prog.hidden = false;
    var i = 0;
    (function next() {
        if (stop || i >= times.length) {
            extractAbort = null; $('fxExtractGo').hidden = false; $('fxExtractStop').hidden = true; prog.hidden = true;
            $('fxExtractText').textContent = T.extract_done.replace(':n', i);
            stageEvt('extract', { n: i, fps: fps });
            return;
        }
        var tt = times[i++];
        FX.captureAt(tt).then(function (img) { return FX.addCaptured(img, tt, FX.frameOf(tt)); }).then(function () {
            prog.firstElementChild.style.width = Math.round(i / times.length * 100) + '%';
            $('fxExtractText').textContent = i + ' / ' + times.length;
            if (i % 20 === 0) setTimeout(next, 40); else next();
        }).catch(function () { next(); });
    })();
});
$('fxExtractStop').addEventListener('click', function () { if (extractAbort) extractAbort(); });

/* ───────────────────────── panel 2 · geometry ───────────────────────── */
document.querySelectorAll('[data-geom]').forEach(function (b) { b.addEventListener('click', function () {
    if (!S.source) return;
    seg(b);
    var key = b.dataset.geom, val = b.dataset.val;
    if (key === 'deint') FX.setGeomSetting('deint', val).then(function () { if (val !== 'off') toast(T.deint_done, 'success'); });
    else if (key === 'par') FX.setGeomSetting('par', parseFloat(val)).then(function () { if (val !== '1') toast(T.par_done, 'success'); });
    stageEvt('geom_' + key, { value: val });
}); });
document.querySelectorAll('[data-rotate]').forEach(function (b) { b.addEventListener('click', function () {
    if (!S.source) return;
    var q = ((S.geom.rotate + parseInt(b.dataset.rotate, 10)) % 4 + 4) % 4;
    FX.setGeomSetting('rotate', q).then(function () { toast(T.rotate_done, 'success'); });
}); });
var undistDebounce = null;
$('fxUndistK').addEventListener('input', function () {
    var k = parseFloat(this.value); $('fxUndistKVal').textContent = k.toFixed(2);
    clearTimeout(undistDebounce); undistDebounce = setTimeout(function () { FX.setGeomSetting('k1', k); }, 120);
});
document.querySelectorAll('[data-undist]').forEach(function (b) { b.addEventListener('click', function () {
    var k = parseFloat(b.dataset.undist); $('fxUndistK').value = k; $('fxUndistKVal').textContent = k.toFixed(2); FX.setGeomSetting('k1', k).then(function () { if (k) toast(T.undist_done, 'success'); });
}); });
$('fxGeomReset').addEventListener('click', function () { FX.resetGeometry().then(function () { syncGeomUI(); toast(T.geom_reset_done, 'success'); }); });
function syncGeomUI() {
    var g = S.geom;
    document.querySelectorAll('[data-geom=deint]').forEach(function (b) { var on = b.dataset.val === g.deint; b.classList.toggle('is-active', on); b.setAttribute('aria-checked', on ? 'true' : 'false'); });
    document.querySelectorAll('[data-geom=par]').forEach(function (b) { var on = Math.abs(parseFloat(b.dataset.val) - g.par) < 0.001; b.classList.toggle('is-active', on); b.setAttribute('aria-checked', on ? 'true' : 'false'); });
    $('fxUndistK').value = g.k1; $('fxUndistKVal').textContent = (+g.k1).toFixed(2);
    drawPersp();
}
FX.on('geom', syncGeomUI);
FX.on('actions_dropped', function () { setRoi(null); });
FX.on('comb', function (c) {
    var n = $('fxCombNote'); n.hidden = !c.interlaced;
    // The note stays on the Geometry panel; the toast says it ONCE per file —
    // a DVR that weaves one frame weaves them all, and a warning on every
    // arrow press is noise, not information.
    if (c.interlaced && S.geom.deint === 'off' && !UI.combToasted) { UI.combToasted = true; toast(T.comb_detected, 'warning'); }
});

/* ───────────────────────── panel 3 · enhance ───────────────────────── */
var STEP_DEFS = {
    deblur:   { label: T.st_deblur, params: [['angle', T.st_deblur_angle, 0, 180, 1, '°'], ['len', T.st_deblur_len, 3, 31, 2, ' px'], ['noise', T.st_deblur_noise, 0.001, 0.1, 0.001, '']] },
    optical:  { label: T.st_optical, params: [['sigma', T.st_optical_sigma, 0.3, 6, 0.1, ' px'], ['iters', T.st_optical_iters, 1, 50, 1, '']] },
    denoise:  { label: T.st_denoise, params: [['strength', T.st_denoise_strength, 1, 30, 1, '']] },
    tone:     { label: T.st_tone, params: [['brightness', T.st_brightness, -100, 100, 1, ''], ['contrast', T.st_contrast, -100, 100, 1, ''], ['gamma', T.st_gamma, 0.2, 4, 0.05, ''], ['saturation', T.st_saturation, -100, 100, 1, '']] },
    levels:   { label: T.st_levels, params: [['black', T.st_black, 0, 200, 1, ''], ['white', T.st_white, 55, 255, 1, ''], ['gamma', T.st_gamma, 0.2, 4, 0.05, '']] },
    clahe:    { label: T.st_clahe, params: [['clip', T.st_clahe_clip, 1, 10, 0.5, ''], ['tile', T.st_clahe_tile, 2, 16, 1, '']] },
    histeq:   { label: T.st_histeq, modes: [['luma', T.st_histeq_luma], ['rgb', T.st_histeq_rgb]] },
    colorbal: { label: T.st_colorbal, params: [['sr', T.st_shadows + ' R', -100, 100, 1, ''], ['sg', T.st_shadows + ' G', -100, 100, 1, ''], ['sb', T.st_shadows + ' B', -100, 100, 1, ''], ['mr', T.st_mid + ' R', -100, 100, 1, ''], ['mg', T.st_mid + ' G', -100, 100, 1, ''], ['mb', T.st_mid + ' B', -100, 100, 1, ''], ['hr', T.st_highlights + ' R', -100, 100, 1, ''], ['hg', T.st_highlights + ' G', -100, 100, 1, ''], ['hb', T.st_highlights + ' B', -100, 100, 1, '']] },
    sharpen:  { label: T.st_sharpen, params: [['radius', T.st_sharpen_radius, 1, 10, 1, ' px'], ['amount', T.st_sharpen_amount, 0, 5, 0.1, '']] },
    edge:     { label: T.st_edge, params: [['amount', T.st_edge_amount, 0.1, 3, 0.1, '']] },
    invert:   { label: T.st_invert },
};
function buildChain() {
    var host = $('fxChain'); host.innerHTML = '';
    FX.CHAIN_ORDER.forEach(function (key) {
        var def = STEP_DEFS[key], st = S.enh.steps[key];
        var el = document.createElement('div'); el.className = 'fx-step'; el.dataset.step = key;
        var h = document.createElement('div'); h.className = 'fx-step-h';
        var sw = document.createElement('button'); sw.type = 'button'; sw.className = 'fx-switch'; sw.setAttribute('role', 'switch'); sw.setAttribute('aria-label', def.label);
        var lab = document.createElement('span'); lab.className = 'fx-lab'; lab.textContent = def.label;
        var sum = document.createElement('span'); sum.className = 'fx-step-sum';
        h.appendChild(sw); h.appendChild(lab); h.appendChild(sum);
        if (def.params || def.modes) { h.insertAdjacentHTML('beforeend', '<svg class="fx-step-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" aria-hidden="true"><path d="M9 6l6 6-6 6"/></svg>'); }
        el.appendChild(h);
        var body = document.createElement('div'); body.className = 'fx-step-b';
        if (def.params) def.params.forEach(function (p) {
            var row = document.createElement('div'); row.className = 'fx-slider';
            var id = 'fxp_' + key + '_' + p[0];
            row.innerHTML = '<label for="' + id + '">' + p[1] + '</label><span class="fx-val" data-val="' + p[0] + '"></span><input type="range" class="fx-range" id="' + id + '" data-param="' + p[0] + '" min="' + p[2] + '" max="' + p[3] + '" step="' + p[4] + '">';
            body.appendChild(row);
        });
        if (def.modes) {
            var sg = document.createElement('div'); sg.className = 'fx-seg fx-seg-grow'; sg.setAttribute('role', 'radiogroup');
            def.modes.forEach(function (m) { var b = document.createElement('button'); b.type = 'button'; b.className = 'fx-seg-b'; b.dataset.mode = m[0]; b.textContent = m[1]; b.addEventListener('click', function () { seg(b); FX.setStep(key, { mode: m[0] }, { now: true }); }); sg.appendChild(b); });
            body.appendChild(sg);
        }
        if (key === 'deblur') {
            var row = document.createElement('div'); row.className = 'fx-row';
            row.innerHTML = '<canvas class="fx-kernel" id="fxKernel" width="44" height="44" aria-hidden="true"></canvas><button type="button" class="fx-btn fx-btn-quiet fx-btn-sm" id="fxDeblurSearch" style="flex:1">' + T.st_deblur_search + '</button>';
            body.appendChild(row);
        }
        el.appendChild(body);
        host.appendChild(el);

        sw.addEventListener('click', function () { var on = !S.enh.steps[key].on; FX.setStep(key, { on: on }, { now: true }); if (on) el.classList.add('is-open'); stageEvt('step_' + key, { on: on }); });
        h.addEventListener('click', function (e) { if (e.target === sw || sw.contains(e.target)) return; if (def.params || def.modes) el.classList.toggle('is-open'); });
        body.querySelectorAll('input[data-param]').forEach(function (inp) {
            var dragging = false;
            inp.addEventListener('pointerdown', function () { if (!dragging) { dragging = true; FX.pushHistory(); } });
            inp.addEventListener('pointerup', function () { dragging = false; });
            inp.addEventListener('input', function () {
                var patch = {}; patch[inp.dataset.param] = parseFloat(inp.value);
                if (!S.enh.steps[key].on) patch.on = true;
                FX.setStep(key, patch, { noHistory: true });
                syncStep(key);
            });
        });
    });
    var ds = $('fxDeblurSearch');
    if (ds) ds.addEventListener('click', function () {
        if (!S.base) return;
        ds.disabled = true; ds.textContent = T.st_deblur_searching;
        FX.searchDeblur(UI.roi && UI.roi.w > 16 ? UI.roi : null).then(function (best) {
            ds.disabled = false; ds.textContent = T.st_deblur_search;
            if (!best) return;
            FX.setStep('deblur', { on: true, angle: best.angle, len: best.size }, { now: true });
            toast(T.st_deblur_found.replace(':a', best.angle).replace(':l', best.size), 'success');
            stageEvt('deblur_search', { angle: best.angle, len: best.size });
        }).catch(function () { ds.disabled = false; ds.textContent = T.st_deblur_search; toast(T.err_worker, 'error'); });
    });
    syncChain();
}
function fmtParam(key, p, v) {
    if (key === 'deblur' && p === 'noise') return (+v).toFixed(3);
    if (key === 'optical' && p === 'sigma') return (+v).toFixed(1);
    if ((key === 'tone' || key === 'levels') && p === 'gamma') return (+v).toFixed(2);
    if (key === 'clahe' && p === 'clip') return (+v).toFixed(1);
    if (key === 'sharpen' && p === 'amount') return (+v).toFixed(1);
    if (key === 'edge') return (+v).toFixed(1);
    return String(v);
}
function syncStep(key) {
    var el = $('fxChain').querySelector('[data-step="' + key + '"]'); if (!el) return;
    var st = S.enh.steps[key], def = STEP_DEFS[key];
    el.classList.toggle('is-on', !!st.on);
    el.querySelector('.fx-switch').setAttribute('aria-checked', st.on ? 'true' : 'false');
    var sums = [];
    el.querySelectorAll('input[data-param]').forEach(function (inp) {
        var p = inp.dataset.param; if (document.activeElement !== inp) inp.value = st[p];
        var v = el.querySelector('[data-val="' + p + '"]'); if (v) v.textContent = fmtParam(key, p, st[p]) + (def.params.find(function (x) { return x[0] === p; })[5] || '');
    });
    if (def.params && st.on) sums = def.params.slice(0, 3).map(function (p) { return fmtParam(key, p[0], st[p[0]]); });
    if (def.modes) el.querySelectorAll('[data-mode]').forEach(function (b) { var on = b.dataset.mode === st.mode; b.classList.toggle('is-active', on); b.setAttribute('aria-checked', on ? 'true' : 'false'); });
    el.querySelector('.fx-step-sum').textContent = st.on ? sums.join(' · ') : '';
    if (key === 'deblur') drawKernel(st.angle, st.len);
}
function syncChain() { FX.CHAIN_ORDER.forEach(syncStep); $('fxStrength').value = Math.round(S.enh.strength * 100); $('fxStrengthVal').textContent = Math.round(S.enh.strength * 100) + '%'; }
function drawKernel(angle, len) {
    var c = $('fxKernel'); if (!c) return; var x = c.getContext('2d');
    x.fillStyle = '#000'; x.fillRect(0, 0, 44, 44);
    x.strokeStyle = '#fff'; x.lineWidth = 2; x.lineCap = 'round';
    var r = Math.min(20, len * 0.65), a = angle * Math.PI / 180;
    x.beginPath(); x.moveTo(22 - r * Math.cos(a), 22 - r * Math.sin(a)); x.lineTo(22 + r * Math.cos(a), 22 + r * Math.sin(a)); x.stroke();
}
FX.on('enh', syncChain);
$('fxStrength').addEventListener('input', function () { FX.setStrength(parseInt(this.value, 10) / 100); $('fxStrengthVal').textContent = this.value + '%'; render(); });
document.querySelectorAll('[data-preset]').forEach(function (b) { b.addEventListener('click', function () {
    if (!S.base) return;
    document.querySelectorAll('[data-preset]').forEach(function (x) { x.classList.toggle('is-active', x === b && b.dataset.preset !== 'none'); });
    if (b.dataset.preset === 'none') FX.resetEnhance(); else { FX.applyPreset(b.dataset.preset); toast(T.preset_applied, 'success'); }
    stageEvt('preset_' + b.dataset.preset);
}); });
$('fxBatch').addEventListener('click', function () {
    var sel = S.captured.filter(function (c) { return c.selected; });
    if (!sel.length) { toast(T.batch_none, 'warning'); return; }
    var btn = this; btn.disabled = true; var label = btn.textContent;
    FX.batchApply(function (p) { btn.textContent = T.batch_busy + ' ' + Math.round(p * 100) + '%'; }).then(function (n) {
        btn.disabled = false; btn.textContent = label; toast(T.batch_done.replace(':n', n), 'success'); stageEvt('batch', { n: n });
    }).catch(function () { btn.disabled = false; btn.textContent = label; toast(T.err_worker, 'error'); });
});

/* ───────────────────────── plates on the picture ─────────────────────────
   The picture is searched for plates as soon as it is on the screen (plates.js on the site's
   PlateDetect). Each plate is a card: the plate straightened and enlarged, how much real detail
   it carries, and the action that reads it — on a video, assembling it from the neighbouring
   frames first, which is what decides at these widths (fused vs one frame: 24 : 1 on the stand).
   A box the person draws is searched too: the plate inside it is what gets read and followed,
   not the car round it (a box round the car made the tracker answer «1 of 60 frames»). */
var PL = window.FXPlates;
UI.plates = []; UI.plateSel = -1; UI.roiPlate = null;
/* Whose plate it is decides which network reads it. «Auto» is the default: on the Commons stands
   (86 Russian, 108 foreign, 206 Brazilian plates) showing the more confident of the two readers
   came within 1 pp of an oracle that knows the country, in every group and at every width. The
   person can still name it — a choice is remembered. */
var KIND_OPTS = Array.from(($('fxPlateKind') || {}).options || []).map(function (o) { return o.value; });
UI.plateKind = 'auto';
try { var savedKind = window.__toolSettings && window.__toolSettings.load(CFG.slug, 'plateKind', null); if (savedKind && KIND_OPTS.indexOf(savedKind) >= 0) UI.plateKind = savedKind; } catch (_) {}
if ($('fxPlateKind')) {
    $('fxPlateKind').value = UI.plateKind;
    $('fxPlateKind').addEventListener('change', function () {
        UI.plateKind = this.value;
        try { window.__toolSettings && window.__toolSettings.save(CFG.slug, 'plateKind', UI.plateKind); } catch (_) {}
        stageEvt('plate_kind', { kind: UI.plateKind });
        // the answer on the screen may be another reader's: read again, the person asked for it
        if (!$('fxPlateOut').hidden) readPlate('kind');
    });
}

function platesState(text) { var el = $('fxPlatesState'); el.textContent = text || ''; el.hidden = !text; }
function activePlate() {
    if (UI.roi && UI.roi.w > 4) return UI.roiPlate;          // a box drawn: the plate in it, or none
    return UI.plateSel >= 0 ? UI.plates[UI.plateSel] || null : null;
}
function drawPlates() {
    var svg = $('fxPlatesSvg'); if (!svg) return;
    var list = UI.plates.slice(), act = activePlate();
    if (UI.roiPlate && list.indexOf(UI.roiPlate) < 0) list.push(UI.roiPlate);
    if (!list.length || !UI.imgW || S.sourceMeta.tracked) { svgShow(svg, false); svg.innerHTML = ''; return; }
    svgShow(svg, true); svg.setAttribute('viewBox', '0 0 ' + UI.imgW + ' ' + UI.imgH);
    var inv = 1 / UI.view.z, html = '';
    list.forEach(function (p) {
        var i = UI.plates.indexOf(p), on = p === act, cls = on ? ' class="is-active"' : '';
        var pts = p.quad.map(function (q) { return q[0].toFixed(1) + ',' + q[1].toFixed(1); }).join(' ');
        html += '<polygon data-plate="' + (i >= 0 ? i : 'roi') + '"' + cls + ' points="' + pts + '"/>';
        var tx = Math.min(p.quad[0][0], p.quad[3][0]), ty = Math.min(p.quad[0][1], p.quad[1][1]);
        var label = i >= 0 ? String(i + 1) : '⌗';
        html += '<g transform="translate(' + tx.toFixed(1) + ' ' + ty.toFixed(1) + ') scale(' + inv + ')"><rect class="fx-plate-tag' + (on ? ' is-active' : '') + '" x="0" y="-19" width="18" height="17" rx="4"/><text x="5" y="-6">' + label + '</text></g>';
    });
    svg.innerHTML = html;
}
function selectPlate(i, opts) {
    if (i < 0 || i >= UI.plates.length) return;
    UI.plateSel = i;
    if (UI.roi) setRoi(null);
    UI.roiPlate = null;
    drawPlates(); renderPlateCards();
    if (opts && opts.show) showPlateOnStage(UI.plates[i]);
}
/* Zoom the stage onto a plate: the card's picture is small, the stage shows its pixels. */
function showPlateOnStage(p) {
    var b = PL.bboxOf(p.quad), vw = viewport.clientWidth, vh = viewport.clientHeight;
    if (!vw || !vh) return;
    var z = Math.max(0.05, Math.min(32, Math.min(vw * 0.6 / Math.max(8, b.w), vh * 0.5 / Math.max(4, b.h))));
    UI.view.z = z; UI.view.x = vw / 2 - (b.x + b.w / 2) * z; UI.view.y = vh / 2 - (b.y + b.h / 2) * z; UI.view.fitted = false;
    applyView();
}
function plateVerdictText(p) {
    var v = p.verdict, video = S.kind === 'video', eff = Math.round(p.eff);
    var say = v === 'ok' ? T.plate_say_ok
        : v === 'hard' ? (video ? T.plate_say_hard_video : T.plate_say_hard_photo)
        : T.plate_say_tiny.replace(':eff', eff) + (video ? ' ' + T.plate_say_tiny_video : '');
    if (!video && v !== 'ok' && S.base && PL.screenshotLike(S.source.width, S.source.height)) say += ' ' + T.plate_say_screenshot;
    return say;
}
function plateCard(p, idx) {
    var card = document.createElement('div');
    card.className = 'fx-pcard' + (p === activePlate() ? ' is-active' : '');
    var show = document.createElement('button'); show.type = 'button'; show.className = 'fx-pcard-img'; show.title = T.plate_show;
    show.setAttribute('aria-label', T.plate_show);
    try {
        var im = PL.rectifyRGBA(S.base, p.quad, { margin: 0.08, marginY: 0.2 });
        var c = document.createElement('canvas'); c.width = im.width; c.height = im.height;
        c.getContext('2d').putImageData(im, 0, 0); show.appendChild(c);
    } catch (_) {}
    show.addEventListener('click', function () { if (idx >= 0) selectPlate(idx, { show: true }); else showPlateOnStage(p); });
    card.appendChild(show);
    var meta = document.createElement('div'); meta.className = 'fx-pcard-meta';
    var vLabel = { ok: T.plate_v_ok, hard: T.plate_v_hard, tiny: T.plate_v_tiny }[p.verdict];
    meta.innerHTML = '<b>' + (idx >= 0 ? '№' + (idx + 1) : '⌗') + '</b><span class="fx-mono">'
        + esc(T.plate_card_size.replace(':w', Math.round(p.w)).replace(':h', Math.round(p.h)).replace(':eff', Math.round(p.eff)))
        + '</span><span class="fx-pcard-v ' + p.verdict + '">' + esc(vLabel) + '</span>';
    card.appendChild(meta);
    var note = document.createElement('p'); note.className = 'fx-note'; note.textContent = plateVerdictText(p); card.appendChild(note);
    var row = document.createElement('div'); row.className = 'fx-row';
    var mk = function (label, cls, fn) { var b = document.createElement('button'); b.type = 'button'; b.className = 'fx-btn ' + cls; b.textContent = label; b.addEventListener('click', fn); row.appendChild(b); return b; };
    var pick = function () { if (idx >= 0 && UI.plateSel !== idx) selectPlate(idx); };
    if (S.kind === 'video' && S.video.loaded) {
        mk(T.plate_fuse_btn, 'fx-btn-cta', function () { pick(); runTrack({ plate: p, thenRead: true }); }).dataset.plateFuse = '1';
        mk(T.plate_frame_btn, 'fx-btn-quiet', function () { pick(); readPlate('card_frame', p); });
    } else {
        mk(T.plate_read_btn, 'fx-btn-cta', function () { pick(); readPlate('card', p); }).dataset.plateRead = '1';
    }
    card.appendChild(row);
    return card;
}
function renderPlateCards() {
    var host = $('fxPlatesList'); host.innerHTML = '';
    if (S.sourceMeta.tracked) return;
    var list = UI.plates.map(function (p, i) { return { p: p, i: i }; });
    if (UI.roiPlate && UI.plates.indexOf(UI.roiPlate) < 0) list.unshift({ p: UI.roiPlate, i: -1 });
    list.forEach(function (it) { host.appendChild(plateCard(it.p, it.i)); });
}
var plateScanTimer = null, plateScanImg = null;
function schedulePlateScan() {
    clearTimeout(plateScanTimer);
    if (!PL || !window.PlateDetect) return;
    if (S.sourceMeta.tracked) {
        UI.plates = []; UI.plateSel = -1; UI.roiPlate = null; drawPlates(); renderPlateCards();
        platesState(T.plates_tracked); $('fxPlatesManualRow').hidden = true;
        return;
    }
    if (UI.playing || !S.base) return;
    var img = S.base;
    if (img === plateScanImg) return;                       // the same picture: its plates are known
    plateScanTimer = setTimeout(function () { runPlateScan(img); }, S.kind === 'video' ? 350 : 0);
}
function runPlateScan(img) {
    if (img !== S.base || UI.playing) return;
    plateScanImg = img;
    UI.plates = []; UI.plateSel = -1; UI.roiPlate = null; drawPlates(); renderPlateCards();
    $('fxPlatesManualRow').hidden = true;
    platesState(T.plates_scanning);
    var t0 = performance.now(), msInit = 0;
    // the network loads once per page (runtime + 3.9 MB): timed apart from the search itself
    window.PlateDetect.ensure().then(function () {
        msInit = Math.round(performance.now() - t0);
        return PL.scan(img, S.kind === 'video' ? 'frame' : 'photo');
    }).then(function (list) {
        if (!list || img !== S.base) return;
        UI.plates = list.slice(0, 4);
        UI.plateSel = UI.plates.length ? 0 : -1;
        var best = UI.plates[0];
        if (best) platesState(UI.plates.length > 1 ? T.plates_found.replace(':n', UI.plates.length) : '');
        else {
            platesState(T.plates_none + (S.kind === 'video' ? ' ' + T.plates_none_hint : '')
                + (S.kind === 'image' && PL.screenshotLike(img.width, img.height) ? ' ' + T.plate_say_screenshot : ''));
            $('fxPlatesManualRow').hidden = false;
        }
        drawPlates(); renderPlateCards();
        stageEvt('plates_scan', {
            kind: S.kind, n: UI.plates.length, ms: Math.round(performance.now() - t0), ms_init: msInit, w: img.width, h: img.height,
            bw: best ? Math.round(best.w) : 0, eff: best ? Math.round(best.eff) : 0, v: best ? best.verdict : '',
            s: best ? Math.round(best.score * 100) / 100 : 0, shot: S.kind === 'image' && PL.screenshotLike(img.width, img.height) ? 1 : 0
        });
    }).catch(function (e) {
        if (img !== S.base) return;
        platesState(T.plates_load_failed); $('fxPlatesManualRow').hidden = false;
        stageEvt('plates_scan_failed', { code: String((e && (e.code || e.message)) || 'unknown').slice(0, 60) });
    });
}
FX.on('base', schedulePlateScan);
$('fxPlatesDraw').addEventListener('click', function () {
    setTool('roi'); cue(T.plate_need_box, true);
    try { stage.scrollIntoView({ block: 'nearest' }); } catch (_) {}
});
/* A box was drawn: is there a plate in it? */
var roiSeq = 0;
function onRoiDrawn(r) {
    UI.roiPlate = null; drawPlates();
    if (!PL || !window.PlateDetect || !S.base || S.sourceMeta.tracked || !r || r.w < 12 || r.h < 6) return;
    var my = ++roiSeq, img = S.base;
    PL.within(img, r).then(function (p) {
        if (my !== roiSeq || UI.roi !== r || img !== S.base) return;
        // no plate seen in the box: a plate-shaped box is still measured and judged
        UI.roiPlate = p || PL.boxPlate(img, r) || null;
        drawPlates(); renderPlateCards();
        cue(p ? T.plates_in_box : T.plates_box_none);
        var q = UI.roiPlate;
        stageEvt('plates_in_box', { found: p ? 1 : 0, boxed: q && q.boxOnly ? 1 : 0, bw: Math.round(r.w), bh: Math.round(r.h), pw: q ? Math.round(q.w) : 0, eff: q ? Math.round(q.eff) : 0, v: q ? q.verdict : '' });
    }).catch(function () {});
}

/* ───────────────────────── reading a plate ─────────────────────────
   A list, not a verdict: the likeliest plates with probabilities — and, for a Russian plate,
   every position with its likeliest symbols and a mask with «?» where no symbol is likely
   enough: the form a request to the police can use together with the car's make and colour.
   Two readers: our network for Russian plates (plate-reader-worker.js), and one for the plates
   of other countries (FX.plateReadIntl). The telemetry carries the mode, the width and a
   confidence bucket — never the plate itself. */
var CYR = { A: 'А', B: 'В', E: 'Е', K: 'К', M: 'М', H: 'Н', O: 'О', P: 'Р', C: 'С', T: 'Т', Y: 'У', X: 'Х' };
function cyr(s) { return String(s).replace(/[ABEKMHOPCTYX]/g, function (c) { return CYR[c]; }); }
function fmtPlate(s) { s = cyr(s); return s.charAt(0) + ' ' + s.slice(1, 4) + ' ' + s.slice(4, 6) + ' ' + s.slice(6); }
function fmtP(p) { return p >= 0.995 ? '>99%' : p < 0.01 ? '<1%' : Math.round(p * 100) + '%'; }
function plateMask(slots) {
    // a position is written out only when its best symbol is at least 60 % likely
    var s = slots.map(function (pos, i) {
        var b = pos[0];
        if (i === 8 && b && b.ch === '') return '';
        return b && b.p >= 0.6 ? cyr(b.ch || '') : '?';
    });
    return s[0] + ' ' + s.slice(1, 4).join('') + ' ' + s.slice(4, 6).join('') + ' ' + s.slice(6).join('');
}
function confClass(p) { return p >= 0.8 ? 'hi' : p >= 0.4 ? 'mid' : 'lo'; }
/* What gets read: the fused picture after an assembly; else the plate (found, picked, or found
   inside the drawn box); else the drawn box itself. */
function readTarget(plate) {
    if (S.sourceMeta.tracked) return { fused: true };
    var p = plate || activePlate();
    if (p && p.boxOnly) return { box: p.box, plate: null, judged: p };
    if (p) return { plate: p };
    if (UI.roi && UI.roi.w >= 8 && UI.roi.h >= 4) return { box: { x: UI.roi.x, y: UI.roi.y, w: UI.roi.w, h: UI.roi.h } };
    return null;
}
/* The person's choice → the readers (FXPlates.auto in plates.js holds the rule and its numbers):
   «Russia» → our Russian net; «other country» → global + eubr, the surer answer; «Auto» → cascade. */
var INTL_PAIR = CFG.plateIntlModels && CFG.plateIntlModels.eubr ? ['global', 'eubr'] : ['global'];
function intlClean(r) {
    r.kind = 'intl';
    r.p0 = r.cands[0] ? r.cands[0].p : 0;      // «who is surer» compares this, before the 2 % cut below
    /* An intl reader always answers ten positions; on a plate with no readable characters its
       «best» is noise («5555 <1%» on the 13-px plate of the 23.10 report). Below 2 % an answer
       is not an answer. */
    r.cands = r.cands.filter(function (c) { return c.p >= 0.02; });
    return r;
}
function readRu(tg, onP) { return FX.plateRead(tg, onP).then(function (r) { r.kind = 'ru'; r.reader = 'ru'; return r; }); }
function readIntl(keys, tg, onP) {
    return FX.plateReadIntl(tg, onP, keys).then(function (rs) { return (Array.isArray(rs) ? rs : [rs]).map(intlClean); });
}
function readOther(tg, onP) {
    return readIntl(INTL_PAIR, tg, onP).then(function (rs) {
        var win = PL.auto.surest(rs);
        if (rs.length > 1) win.pair = rs.map(function (r) { return r.reader; });
        return win;
    });
}
function readAuto(tg, onP) {
    var A = PL.auto, first = A.first(CFG.isRu);
    var rest = A.rest(first).filter(function (k) { return k === 'ru' || INTL_PAIR.indexOf(k) >= 0; });
    var half = function (a, b) { return function (p) { onP(a + (b - a) * p); }; };
    var one = function (k, f) { return k === 'ru' ? readRu(tg, f) : readIntl([k], tg, f).then(function (rs) { return rs[0]; }); };
    return one(first, half(0, 0.4)).then(function (r1) {
        if (!A.needsMore(r1)) { r1.auto = { more: false }; onP(1); return r1; }
        var intlKeys = rest.filter(function (k) { return k !== 'ru'; }), wantRu = rest.indexOf('ru') >= 0;
        var got = [r1];
        return (intlKeys.length ? readIntl(intlKeys, tg, half(0.4, wantRu ? 0.7 : 1)).then(function (rs) { got = got.concat(rs); }, function () {}) : Promise.resolve())
            .then(function () { return wantRu ? readRu(tg, half(0.7, 1)).then(function (r) { got.push(r); }, function () {}) : null; })
            .then(function () {
                var win = A.pick(got);
                win.auto = { more: true, n: got.length };
                return win;
            });
    });
}
var KIND_OF_READER = { ru: 'ru', global: 'intl', eubr: 'intl' };
function kindLabel(v) {
    var o = $('fxPlateKind') && Array.from($('fxPlateKind').options).find(function (x) { return x.value === v; });
    return o ? o.textContent.trim() : v;
}
function readPlate(from, plate) {
    if (!S.base) return;
    var tg = readTarget(plate);
    if (!tg) {
        switchTab('frame'); setTool('roi'); cue(T.plate_need_box, true);
        try { stage.scrollIntoView({ block: 'nearest' }); } catch (_) {}
        return;
    }
    var bill = FX.bill(); if (!bill) return;
    switchTab('frame');                  // the answer, and the wait for the network, are shown in the plate section
    var choice = UI.plateKind;
    var btns = [$('fxPlateGo'), $('fxRoiRead'), $('fxOcrGo')].concat(Array.from(document.querySelectorAll('.fx-pcard .fx-btn')));
    btns.forEach(function (b) { if (b) b.disabled = true; });
    var prog = $('fxPlateProg'); prog.hidden = false; prog.firstElementChild.style.width = '0%';
    var note = $('fxPlateBusy'); note.hidden = false; note.textContent = T.plate_loading;
    var onP = function (p) { prog.firstElementChild.style.width = Math.round(p * 100) + '%'; };
    (choice === 'auto' ? readAuto(tg, onP) : choice === 'ru' ? readRu(tg, onP) : readOther(tg, onP)).then(function (r) {
        bill.commit();
        showPlate(r);
        var p1 = r.cands[0] ? r.cands[0].p : 0;
        stageEvt('plate_read', { kind: r.kind, reader: r.reader, choice: choice, auto_n: r.auto ? (r.auto.n || 1) : null, mode: r.mode, n: r.n, bw: Math.round(r.bw), eff: r.eff == null ? null : Math.round(r.eff), conf: p1 >= 0.8 ? 'hi' : p1 >= 0.4 ? 'mid' : 'lo', from: from || '', src: tg.fused ? 'fused' : tg.plate ? (tg.plate === UI.roiPlate ? 'roi_plate' : 'plate') : 'box', ms: r.msAll, ms_init: r.msInit, ms_run: r.runMs });
        resultDelivered('plate', { mode: r.mode, bw: Math.round(r.bw), kind: r.kind });
    }).catch(function (e) {
        bill.abort();
        var code = (e && e.code) || '';
        toast(code === 'NO_BOX' ? T.plate_need_box : code === 'MODEL_LOAD' || code === 'ORT_LOAD' || code === 'ORT_UNAVAILABLE' ? T.plate_load_failed : code === 'WORKER_CRASHED' ? T.err_crash : T.plate_failed, 'error');
        try { window.__ae && window.__ae('tool_error', { slug: CFG.slug, error_type: 'plate', kind: choice, code: code || String(e && e.message || 'unknown').slice(0, 60) }); } catch (_) {}
        try { console.error(e); } catch (_) {}
    }).then(function () {
        prog.hidden = true; note.hidden = true;
        btns.forEach(function (b) { if (b) b.disabled = false; });
    });
}
function showPlate(r) {
    var out = $('fxPlateOut'); out.hidden = false;
    var intl = r.kind === 'intl';
    var top = r.cands[0];
    var fmt = intl ? function (s) { return s; } : fmtPlate;
    $('fxPlateTop').textContent = top ? fmt(top.text) : '—';
    var conf = $('fxPlateConf'); conf.textContent = top ? fmtP(top.p) : ''; conf.className = 'fx-conf ' + (top ? confClass(top.p) : 'lo');
    var p1 = top ? top.p : 0, nUsed = S.sourceMeta.tracked ? S.sourceMeta.tracked.used : '';
    $('fxPlateVerdict').textContent = (!top && intl ? T.plate_none_read : p1 >= 0.8 ? T.plate_sure : p1 >= 0.4 ? T.plate_likely : intl ? T.plate_unsure_intl : T.plate_unsure)
        + ' ' + (r.mode === 'fused' ? (intl ? T.plate_intl_from_fused : T.plate_from_fused).replace(':n', nUsed) : (intl ? T.plate_intl_from_frame : T.plate_from_frame))
        + (!intl && r.notRu && !r.auto ? ' ' + T.plate_not_ru : '')
        + (intl ? regionLine(r.regions) : '')
        + (r.auto ? ' ' + T.plate_auto.replace(':kind', kindLabel(KIND_OF_READER[r.reader] || (intl ? 'intl' : 'ru'))) : '');
    $('fxPlateSlotsWrap').hidden = intl || !r.slots;
    var mask = '';
    if (!intl && r.slots) {
        mask = plateMask(r.slots);
        $('fxPlateMask').textContent = mask;
        var grid = $('fxPlateSlots'); grid.innerHTML = '';
        r.slots.forEach(function (pos, i) {
            if (i === 8 && pos[0] && pos[0].ch === '' && pos[0].p >= 0.9) return;     // a two-digit region: no ninth position
            var col = document.createElement('div'); col.className = 'fx-pslot' + (i === 6 ? ' is-reg' : '');
            pos.forEach(function (c, k) {
                if (c.p < 0.03 && k) return;
                var row = document.createElement('div'); row.className = 'fx-pslot-r' + (k ? '' : ' is-top');
                // a narrow column: the number without «%» (the heading says it is percent)
                row.innerHTML = '<b class="fx-mono">' + (c.ch === '' ? '·' : esc(cyr(c.ch))) + '</b><span>' + fmtP(c.p).replace('%', '') + '</span>';
                col.appendChild(row);
            });
            grid.appendChild(col);
        });
    }
    var list = $('fxPlateCands'); list.innerHTML = '';
    r.cands.forEach(function (c) {
        if (c.p < 0.005) return;
        var chip = document.createElement('span'); chip.className = 'fx-chip'; chip.style.cursor = 'default';
        chip.innerHTML = '<span class="fx-mono">' + esc(fmt(c.text)) + '</span><span class="fx-conf ' + confClass(c.p) + '">' + fmtP(c.p) + '</span>';
        list.appendChild(chip);
    });
    $('fxPlateCredit').textContent = r.reader === 'eubr' ? T.plate_credit_eubr : intl ? T.plate_credit_intl : T.plate_credit;
    $('fxPlateNote').textContent = intl ? T.plate_note_intl : T.plate_note;      // no mask for other countries' plates
    S.plateRead = { at: S.sourceMeta.time, mode: r.mode, kind: r.kind, cands: r.cands.slice(0, 10), mask: mask,
        lines: r.cands.filter(function (c) { return c.p >= 0.005; }).map(function (c) { return fmt(c.text) + ' — ' + fmtP(c.p); }) };
    try { out.scrollIntoView({ block: 'nearest' }); } catch (_) {}
}
/* The country the intl reader's region head thinks the plate is from, named in the page's language;
   said only when it is fairly sure (a guess about the country is not what the person asked for). */
function regionLine(regs) {
    var top = regs && regs[0];
    if (!top || !top.name || !/^[A-Z]{2}$/.test(top.name) || top.p < 0.6) return '';
    var name = top.name;
    try { name = new Intl.DisplayNames([window.__intlLocale ? window.__intlLocale() : 'en'], { type: 'region' }).of(top.name) || top.name; } catch (_) {}
    return ' ' + T.plate_region.replace(':country', name).replace(':p', fmtP(top.p));
}
function hidePlate() { $('fxPlateOut').hidden = true; S.plateRead = null; }
FX.on('source', hidePlate);
$('fxPlateGo').addEventListener('click', function () { readPlate('track'); });
$('fxRoiRead').addEventListener('click', function () { readPlate('roi'); });
$('fxPlateCopy').addEventListener('click', function () {
    var pr = S.plateRead; if (!pr) return;
    var lines = [T.plate_copy_head, pr.lines.join('\n')];
    if (pr.mask) lines.push(T.plate_mask + ': ' + pr.mask);
    navigator.clipboard.writeText(lines.join('\n')).then(function () { toast(T.copied, 'success'); }, function () { toast(T.copy_failed, 'error'); });
});

/* ───────────────────────── panel 4 · read ───────────────────────── */
function syncOcrMode() {
    var plate = UI.ocrMode === 'plate';
    $('fxOcrLang').hidden = plate;
    $('fxOcrPlateNote').hidden = !plate;
}
document.querySelectorAll('[data-ocr-mode]').forEach(function (b) {
    if (b.classList.contains('is-active')) UI.ocrMode = b.dataset.ocrMode;
    b.addEventListener('click', function () { seg(b); UI.ocrMode = b.dataset.ocrMode; syncOcrMode(); });
});
syncOcrMode();
$('fxOcrGo').addEventListener('click', function () {
    if (!S.current) return;
    // a plate is read by the plate flow (its reader, its answer, its place on the Frame tab)
    if (UI.ocrMode === 'plate') { readPlate('ocr'); return; }
    var btn = this; btn.disabled = true; btn.textContent = T.ocr_loading;
    var lang = $('fxOcrLang').value;
    var plate = false;
    var langs = lang === 'eng' ? 'eng' : lang + '+eng';
    // the box, when one is drawn: the text the person pointed at, not every sign in the frame
    var full = FX.composite(), r = UI.roi && UI.roi.w >= 8 && UI.roi.h >= 6 ? UI.roi : null;
    var src = FX.imageDataToCanvas(r ? FX.cropImageData(full, r) : full);
    // Small text is what the readers fail on; enlarging a small box helps them.
    var scale = r && src.width < 600 ? Math.min(4, Math.ceil(600 / src.width)) : 1;
    if (scale > 1) { var big = document.createElement('canvas'); big.width = src.width * scale; big.height = src.height * scale; var bx = big.getContext('2d'); bx.imageSmoothingEnabled = true; bx.imageSmoothingQuality = 'high'; bx.drawImage(src, 0, 0, big.width, big.height); src = big; }
    var workerRef = null;
    // The shared OCR door (PP-OCRv5 for Latin/Cyrillic, Tesseract otherwise). A
    // plate is one line: the Tesseract lane gets PSM 7; either lane's text is
    // post-filtered to A–Z 0–9 below, which is what the whitelist used to do.
    (window.OcrEngine ? Promise.resolve() : Promise.reject(new Error('OCR_LOAD'))).then(function () {
        btn.textContent = T.ocr_busy;
        return window.OcrEngine.open(langs, {
            slug: 'forensic-enhancer',
            tesseractUrl: CFG.tesseract, tesseractFallbackUrl: CFG.tesseractFallback,
            tesseract: { workerPath: CFG.tesseractWorker || undefined, corePath: CFG.tesseractCore || undefined, psm: plate ? '7' : '3' },
        });
    }).then(function (w) {
        workerRef = w;
        return w.recognize(src);
    }).then(function (d) {
        d = d || {};
        var text = String(d.text || '').trim();
        if (plate) text = text.replace(/[^A-Z0-9\s]/g, '').replace(/\s+/g, ' ').trim();
        var out = $('fxOcrOut'); out.hidden = false;
        $('fxOcrText').textContent = text || T.ocr_none;
        var cands = $('fxOcrCands'); cands.innerHTML = '';
        (d.words || []).filter(function (w) { return w.text && w.text.trim(); }).slice(0, 12).forEach(function (w) {
            var c = Math.round(w.confidence || 0);
            var chip = document.createElement('span'); chip.className = 'fx-chip'; chip.style.cursor = 'default';
            chip.innerHTML = '<span class="fx-mono">' + esc(w.text.trim()) + '</span><span class="fx-conf ' + (c >= 75 ? 'hi' : c >= 45 ? 'mid' : 'lo') + '">' + c + '%</span>';
            cands.appendChild(chip);
        });
        stageEvt('ocr', { mode: UI.ocrMode, conf: Math.round(d.confidence || 0), len: text.length, engine: d.engine });
    }).catch(function (e) { toast(T.ocr_failed, 'error'); try { console.error(e); } catch (_) {} })
    .then(function () { btn.disabled = false; btn.textContent = T.ocr_go; if (workerRef) { try { workerRef.close(); } catch (_) {} } });
});
$('fxOcrCopy').addEventListener('click', function () { var t = $('fxOcrText').textContent; if (t) navigator.clipboard.writeText(t).then(function () { toast(T.copied, 'success'); }, function () { toast(T.copy_failed, 'error'); }); });
function esc(s) { return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;'); }

document.querySelectorAll('[data-ai-scale]').forEach(function (b) { b.addEventListener('click', function () { seg(b); UI.aiScale = parseInt(b.dataset.aiScale, 10); }); });
$('fxAiGo').addEventListener('click', function () {
    if (!S.current) return;
    var btn = this; btn.disabled = true;
    var prog = $('fxAiProg'); prog.hidden = false; prog.firstElementChild.style.width = '0%';
    var note = $('fxAiNote'); note.textContent = T.ai_model;
    // the region the person pointed at: the box, or the plate picked on the frame (with some car round it)
    var reg = UI.roi && UI.roi.w >= 8 && UI.roi.h >= 6 ? { x: Math.round(UI.roi.x), y: Math.round(UI.roi.y), w: Math.round(UI.roi.w), h: Math.round(UI.roi.h) } : null;
    var ap = !reg && activePlate();
    if (ap && S.base) { var bb = PL.bboxOf(ap.quad), mx = bb.w * 0.5, my = bb.h * 1.2; var rx = Math.max(0, Math.floor(bb.x - mx)), ry = Math.max(0, Math.floor(bb.y - my)); reg = { x: rx, y: ry, w: Math.min(S.base.width - rx, Math.ceil(bb.w + 2 * mx)), h: Math.min(S.base.height - ry, Math.ceil(bb.h + 2 * my)) }; }
    var aiT0 = performance.now();
    FX.aiUpscale(UI.aiScale, function (p) {
        prog.firstElementChild.style.width = Math.round(p.p * 100) + '%';
        note.textContent = p.phase === 'model' ? T.ai_model : T.ai_busy;
    }, reg).then(function () {
        prog.hidden = true; btn.disabled = false; note.textContent = T.ai_done; toast(T.ai_done, 'success'); render();
        stageEvt('ai_upscale', { scale: UI.aiScale, w: S.aiResult.img.width, h: S.aiResult.img.height });
    }).catch(function (e) {
        prog.hidden = true; btn.disabled = false;
        var m = String(e && e.message || '');
        note.textContent = m === 'AI_TOO_BIG' ? T.ai_too_big : m === 'AI_UNSUPPORTED' ? T.ai_unsupported : T.ai_failed;
        toast(note.textContent, 'error'); try { console.error(e); } catch (_) {}
        // the cause, not only the sentence the person read («Реконструкция не удалась» ×23 told us nothing)
        try { window.__ae && window.__ae('tool_error', { slug: CFG.slug, error_type: 'ai', code: (e && (e.code || e.name)) || '', msg: m.slice(0, 120), scale: UI.aiScale, region: reg ? reg.w + 'x' + reg.h : '', ms: Math.round(performance.now() - aiT0) }); } catch (_) {}
    });
});
$('fxAiRevert').addEventListener('click', function () { FX.aiRevert(); render(); $('fxAiNote').textContent = T.ai_note; });

/* ───────────────────────── panel 5 · verify ───────────────────────── */
function showAnalysisOverlay(img) {
    analysisCanvas.hidden = false;
    if (analysisCanvas.width !== img.width || analysisCanvas.height !== img.height) { analysisCanvas.width = img.width; analysisCanvas.height = img.height; }
    analysisCtx.putImageData(img, 0, 0);
    analysisCanvas.style.opacity = UI.analysisOpacity;
    $('fxOverlayRow').hidden = false;
}
function clearAnalysis() {
    UI.analysis = null; analysisCanvas.hidden = true; analysisCtx.clearRect(0, 0, analysisCanvas.width, analysisCanvas.height);
    $('fxOverlayRow').hidden = true; $('fxLevelRow').hidden = true;
    document.querySelectorAll('[data-analysis]').forEach(function (b) { b.classList.remove('is-on'); });
}
function runAnalysis(algo) {
    var btn = document.querySelector('[data-analysis="' + algo + '"]');
    var params = algo === 'levelsweep' ? { center: parseInt($('fxLevelC').value, 10), width: parseInt($('fxLevelW').value, 10) } : {};
    FX.busy(1, T.analysis_busy);
    FX.runAnalysis(algo, params).then(function (m) {
        FX.busy(-1);
        if (UI.analysis !== algo) return;
        if (m.imageData) showAnalysisOverlay(new ImageData(new Uint8ClampedArray(m.imageData.data), m.imageData.width, m.imageData.height));
        else if (m.result && algo === 'clone') {
            var r = m.result;
            if (!r.matches || !r.matches.length) { toast(T.clone_none, 'info'); clearAnalysis(); return; }
            analysisCanvas.hidden = false; analysisCanvas.width = UI.imgW; analysisCanvas.height = UI.imgH; analysisCanvas.style.opacity = 1;
            analysisCtx.clearRect(0, 0, UI.imgW, UI.imgH);
            r.matches.forEach(function (mm) {
                analysisCtx.strokeStyle = 'rgba(239,68,68,' + (0.3 + mm.similarity * 0.7) + ')'; analysisCtx.lineWidth = 1;
                analysisCtx.beginPath(); analysisCtx.moveTo(mm.x1, mm.y1); analysisCtx.lineTo(mm.x2, mm.y2); analysisCtx.stroke();
                analysisCtx.fillStyle = 'rgba(239,68,68,.4)';
                analysisCtx.fillRect(mm.x1 - r.blockSize / 2, mm.y1 - r.blockSize / 2, r.blockSize, r.blockSize); analysisCtx.fillRect(mm.x2 - r.blockSize / 2, mm.y2 - r.blockSize / 2, r.blockSize, r.blockSize);
            });
            $('fxOverlayRow').hidden = false;
            toast(T.clone_found.replace(':n', r.matches.length), 'warning');
        }
    }).catch(function () { FX.busy(-1); toast(T.err_worker, 'error'); });
    stageEvt('analysis_' + algo);
}
document.querySelectorAll('[data-analysis]').forEach(function (b) { b.addEventListener('click', function () {
    if (!S.current) return;
    var algo = b.dataset.analysis;
    if (UI.analysis === algo) { clearAnalysis(); return; }
    clearAnalysis(); UI.analysis = algo; b.classList.add('is-on');
    $('fxLevelRow').hidden = algo !== 'levelsweep';
    runAnalysis(algo);
}); });
var lvDeb = null;
['fxLevelC', 'fxLevelW'].forEach(function (id) { $(id).addEventListener('input', function () { $(id + 'Val').textContent = this.value; clearTimeout(lvDeb); lvDeb = setTimeout(function () { if (UI.analysis === 'levelsweep') runAnalysis('levelsweep'); }, 140); }); });
$('fxOverlayOp').addEventListener('input', function () { UI.analysisOpacity = this.value / 100; $('fxOverlayOpVal').textContent = this.value + '%'; analysisCanvas.style.opacity = UI.analysisOpacity; });
$('fxOverlayOff').addEventListener('click', clearAnalysis);

$('fxHashGo').addEventListener('click', function () {
    if (!S.file) return;
    if (!(window.crypto && crypto.subtle)) { toast(T.err_browser, 'error'); return; }
    var btn = this, f = S.original || S.file; btn.disabled = true; btn.textContent = T.hash_busy;
    var read = (window.FileBytes && FileBytes.arrayBuffer) ? FileBytes.arrayBuffer(f) : f.arrayBuffer();
    read.then(function (buf) { return Promise.all([crypto.subtle.digest('SHA-256', buf), crypto.subtle.digest('SHA-1', buf)]); }).then(function (hs) {
        if ((S.original || S.file) !== f) return;
        var hex = function (b) { return Array.from(new Uint8Array(b)).map(function (x) { return x.toString(16).padStart(2, '0'); }).join(''); };
        S.hashes = { sha256: hex(hs[0]), sha1: hex(hs[1]) };
        var out = $('fxHashOut'); out.hidden = false;
        out.innerHTML = '<div><b>SHA-256</b><br>' + S.hashes.sha256 + '</div><div style="margin-top:4px"><b>SHA-1</b><br>' + S.hashes.sha1 + '</div><button type="button" class="fx-btn fx-btn-quiet fx-btn-sm" id="fxHashCopy" style="margin-top:6px">' + T.hash_copy + '</button>';
        $('fxHashCopy').addEventListener('click', function () { navigator.clipboard.writeText(S.hashes.sha256).then(function () { toast(T.copied, 'success'); }); });
        stageEvt('hash');
    }).catch(function () { toast(T.err_worker, 'error'); }).then(function () { btn.disabled = false; btn.textContent = T.hash_go; });
});

function loadExif(file) {
    var s = document.createElement('script'); s.src = CFG.exifr;
    s.onload = function () {
        exifr.parse(file, { tiff: true, xmp: true, gps: true }).then(function (x) {
            if (!x || !Object.keys(x).length) return;
            var rows = [];
            var add = function (k, v) { if (v != null && v !== '') rows.push('<dt>' + esc(k) + '</dt><dd>' + esc(String(v)) + '</dd>'); };
            add(T.meta_camera, ((x.Make || '') + ' ' + (x.Model || '')).trim());
            add(T.meta_lens, x.LensModel);
            if (x.ExposureTime) add(T.meta_exposure, (x.ExposureTime < 1 ? '1/' + Math.round(1 / x.ExposureTime) : x.ExposureTime) + ' s' + (x.ISO ? ' · ISO ' + x.ISO : ''));
            if (x.FNumber) add(T.meta_aperture, 'f/' + x.FNumber);
            if (x.FocalLength) add(T.meta_focal, x.FocalLength + ' mm');
            if (x.DateTimeOriginal) add(T.meta_date, new Date(x.DateTimeOriginal).toLocaleString());
            if (x.Software) add(T.meta_software, x.Software + (/photoshop|gimp|lightroom|affinity|snapseed|paint|canva|pixelmator/i.test(x.Software) ? ' — ' + T.meta_edited : ''));
            if (x.latitude && x.longitude) add(T.meta_gps, x.latitude.toFixed(5) + ', ' + x.longitude.toFixed(5));
            if (rows.length) $('fxMeta').innerHTML = rows.join('');
            S.exifRows = rows.length;
        }).catch(function () {});
    };
    document.head.appendChild(s);
}

/* ───────────────────────── film row ───────────────────────── */
var scrub = $('fxScrub'), scrubCanvas = $('fxScrubCanvas');
function updateTime() {
    var t = video.currentTime || 0;
    $('fxTimeNow').textContent = fmtTime(t, true);
    $('fxTimeDur').textContent = '/ ' + fmtTime(S.video.duration);
    $('fxFrameNo').textContent = '#' + FX.frameOf(t) + (S.video.fpsExact ? '' : ' ~');
    if (!UI.scrubbing) scrub.value = S.video.duration ? (t / S.video.duration * 100) : 0;
}
function drawScrubTrace() {
    var c = scrubCanvas, w = c.clientWidth || 300, h = 20;
    c.width = w * (window.devicePixelRatio || 1); c.height = h * (window.devicePixelRatio || 1);
    var x = c.getContext('2d'); x.scale(window.devicePixelRatio || 1, window.devicePixelRatio || 1);
    x.clearRect(0, 0, w, h);
    // captured markers
    S.captured.forEach(function (cp) { var px = S.video.duration ? cp.time / S.video.duration * w : 0; x.fillStyle = cp.best ? 'rgba(255,176,32,.9)' : 'rgba(120,160,255,.6)'; x.fillRect(px - 1, 2, 2, h - 4); });
    var tr = UI.sharpTrace; if (!tr || !tr.length || !S.video.duration) return;
    var max = 0; tr.forEach(function (r) { if (r.score > max) max = r.score; }); if (!max) return;
    x.beginPath();
    tr.forEach(function (r, i) { var px = r.time / S.video.duration * w, py = h - 2 - (r.score / max) * (h - 4); if (i === 0) x.moveTo(px, py); else x.lineTo(px, py); });
    x.strokeStyle = 'rgba(52,199,89,.95)'; x.lineWidth = 1.5; x.stroke();
}
scrub.addEventListener('pointerdown', function () { UI.scrubbing = true; pause(); });
scrub.addEventListener('input', function () { var t = parseFloat(this.value) / 100 * S.video.duration; video.currentTime = t; $('fxTimeNow').textContent = fmtTime(t, true); $('fxFrameNo').textContent = '#' + FX.frameOf(t); });
scrub.addEventListener('change', function () { UI.scrubbing = false; goToTime(parseFloat(this.value) / 100 * S.video.duration); });
function goToTime(t, extra) {
    if (!S.video.loaded) return;
    pause();
    t = Math.max(0, Math.min(S.video.duration, t));
    FX.captureAt(t).then(function (img) {
        updateTime();
        return FX.setSource(img, { time: video.currentTime, frame: FX.frameOf(video.currentTime), best: extra && extra.best });
    }).then(function () { updateFileMeta(); }).catch(captureFailed);
}
/* Кадр не взялся ни с элемента, ни через декодер — сказать, а не молчать
   (раньше здесь не было catch, и отказ уходил в unhandledrejection). */
function captureFailed(e) {
    var tainted = !!(e && e.code === 'TAINTED');
    try { window.__ae && window.__ae('feature_use', { slug: CFG.slug, f: 'capture_failed', tainted: tainted ? 1 : 0, msg: String(e && e.message || e).slice(0, 80) }); } catch (_) {}
    var msg = tainted
        ? (T.err_frame_tainted || 'The browser refused to hand over the pixels of this frame. Try another file or another browser.')
        : (T.err_decode || 'Could not read this frame.');
    if (window.__toast) window.__toast(msg, 'error');
}
function stepFrames(d) { if (!S.video.loaded) return; goToTime(FX.timeOfFrame(FX.frameOf(video.currentTime) + d)); }
$('fxPrevFrame').addEventListener('click', function () { stepFrames(-1); });
$('fxNextFrame').addEventListener('click', function () { stepFrames(1); });
$('fxGoStart').addEventListener('click', function () { goToTime(0); });
$('fxGoEnd').addEventListener('click', function () { goToTime(S.video.duration - 0.5 / S.video.fps); });
var playTick = null;
function play() {
    if (!S.video.loaded || UI.playing) return;
    UI.playing = true; $('fxPlay').setAttribute('aria-pressed', 'true'); $('fxPlay').querySelector('.fx-ic-play').hidden = true; $('fxPlay').querySelector('.fx-ic-pause').hidden = false;
    video.play().catch(function () { pause(); });
    var draw = function () {
        if (!UI.playing) return;
        // While playing the stage shows the raw video (no chain): the chain
        // is for a frame you stopped on.
        if (UI.imgW) { setImageSize(S.video.w, S.video.h); ctx.drawImage(video, 0, 0, S.video.w, S.video.h); }
        updateTime();
        if (video.requestVideoFrameCallback) playTick = video.requestVideoFrameCallback(draw); else playTick = requestAnimationFrame(draw);
    };
    draw();
    video.onended = pause;
}
function pause() {
    if (!UI.playing) return;
    UI.playing = false; video.pause();
    $('fxPlay').setAttribute('aria-pressed', 'false'); $('fxPlay').querySelector('.fx-ic-play').hidden = false; $('fxPlay').querySelector('.fx-ic-pause').hidden = true;
    if (playTick) { if (video.cancelVideoFrameCallback) video.cancelVideoFrameCallback(playTick); else cancelAnimationFrame(playTick); playTick = null; }
    goToTime(video.currentTime);
}
$('fxPlay').addEventListener('click', function () { if (UI.playing) pause(); else play(); });

/* Captured strip */
function renderStrip() {
    var host = $('fxStrip');
    host.querySelectorAll('.fx-thumb').forEach(function (n) { n.remove(); });
    $('fxStripEmpty').hidden = S.captured.length > 0;
    S.captured.forEach(function (c) {
        var b = document.createElement('button'); b.type = 'button'; b.className = 'fx-thumb' + (c.selected ? ' is-selected' : '') + (c.best ? ' is-best' : '') + (c.id === UI.currentCaptured ? ' is-current' : '');
        b.title = fmtTime(c.time, true) + ' · #' + c.frame; b.dataset.id = c.id;
        b.innerHTML = '<img src="' + c.thumb + '" alt=""><span class="fx-thumb-t">' + fmtTime(c.time, true) + '</span>';
        var pressT = null;
        b.addEventListener('click', function (e) {
            if (e.ctrlKey || e.metaKey || e.shiftKey) { c.selected = !c.selected; renderStrip(); return; }
            UI.currentCaptured = c.id;
            FX.setSource(FX.capturedImageData(c), { time: c.time, frame: c.frame }).then(function () { if (S.video.loaded) { video.currentTime = c.time; updateTime(); } updateFileMeta(); });
            renderStrip();
        });
        b.addEventListener('pointerdown', function (e) { if (e.pointerType === 'touch') pressT = setTimeout(function () { c.selected = !c.selected; renderStrip(); pressT = null; }, 500); });
        b.addEventListener('pointerup', function () { if (pressT) { clearTimeout(pressT); pressT = null; } });
        b.addEventListener('pointerleave', function () { if (pressT) { clearTimeout(pressT); pressT = null; } });
        host.appendChild(b);
    });
    var sel = S.captured.filter(function (c) { return c.selected; }).length;
    $('fxBatch').disabled = sel === 0;
    $('fxBatch').textContent = sel ? T.batch + ' (' + sel + ')' : T.batch;
    drawScrubTrace();
}
FX.on('captured', renderStrip);
$('fxStripAll').addEventListener('click', function () { S.captured.forEach(function (c) { c.selected = true; }); renderStrip(); });
$('fxStripNone').addEventListener('click', function () { S.captured.forEach(function (c) { c.selected = false; }); renderStrip(); });

/* ───────────────────────── file open ───────────────────────── */
var VIDEO_EXTS = /\.(mp4|mov|m4v|avi|mkv|webm|wmv|flv|3gp|ogv|ts|mts)$/i, IMAGE_EXTS = /\.(jpe?g|png|webp|bmp|gif|tiff?)$/i;
function openFile(file, meta) {
    var isVideo = (file.type || '').indexOf('video/') === 0 || VIDEO_EXTS.test(file.name);
    var isImage = (file.type || '').indexOf('image/') === 0 || IMAGE_EXTS.test(file.name);
    if (!isVideo && !isImage) { rejectFile(T.not_supported, 'WRONG_FILE_TYPE'); return; }
    closeFile(true);
    S.file = file; S.kind = isVideo ? 'video' : 'image';
    // the EVIDENCE is the file the person chose: a copy made so the browser can play it (rescueVideo)
    // is only how its frames are read — the hash, the report and the daily unit belong to the original
    var orig = (meta && meta.original) || file;
    S.original = orig; S.rescueTier = (meta && meta.tier) || '';
    try { S.limitKey = (window.__toolLimit && window.__toolLimit.fileKey) ? window.__toolLimit.fileKey(orig) : (orig.name + '|' + orig.size + '|' + orig.lastModified); } catch (_) { S.limitKey = orig.name + '|' + orig.size; }
    try { window.__toolLimit && window.__toolLimit.precheck && window.__toolLimit.precheck(CFG.slug, [file]); } catch (_) {}
    editor.dataset.kind = S.kind;
    editor.querySelectorAll('[data-video-only]').forEach(function (el) { el.hidden = !isVideo; });
    $('fxFileName').textContent = file.name;
    startEl.hidden = true; decodeFail.hidden = true; editor.hidden = false;
    UI.plates = []; UI.plateSel = -1; UI.roiPlate = null; plateScanImg = null; clearTimeout(plateScanTimer); drawPlates(); renderPlateCards(); platesState(''); $('fxPlatesManualRow').hidden = true; hidePlate();
    UI.anno.items = []; UI.anno.redo = []; UI.ruler = null; UI.persp = []; setRoi(null); clearAnalysis(); UI.sharpTrace = null; $('fxSharpList').innerHTML = ''; $('fxStackInfo').textContent = ''; $('fxOcrOut').hidden = true; $('fxHashOut').hidden = true; hideTrackResult(); $('fxMeta').innerHTML = '<dd class="fx-note">' + T.meta_none + '</dd>';
    document.querySelectorAll('[data-preset]').forEach(function (x) { x.classList.remove('is-active'); });
    setTool('select'); syncGeomUI(); syncChain();
    stageEvt('upload', { file_type: S.kind, file_size: file.size, file_ext: (file.name.split('.').pop() || '').toLowerCase(), sample: !!(meta && meta.sample) });
    stageTel('upload');
    if (isVideo) {
        if (file.size > 524288000) toast(T.file_warning, 'warning');
        FX.loadVideo(file).then(function () {
            $('fxScrub').max = 100; updateTime();
            FX.mp4Probe(file).then(function () { updateFileMeta(); updateTime(); });
            return goToTime(0);
        }).then(function () { updateFileMeta(); stageTel('frame'); }).catch(function (e) {
            if (meta && meta.rescued) showDecodeFailure(meta.original || file, e && e.message);
            else rescueVideo(file, e);
        });
    } else {
        var url = URL.createObjectURL(file), im = new Image();
        im.onload = function () {
            URL.revokeObjectURL(url);
            var img = FX.drawableToImageData(im, im.naturalWidth, im.naturalHeight);
            FX.setSource(img, { time: 0, frame: 0 }).then(function () { updateFileMeta(); stageTel('frame'); });
        };
        im.onerror = function () {
            URL.revokeObjectURL(url);
            /* An iPhone HEIC saved as «.jpg» passes the uploader's HEIC door (it goes by the name and
               the type) and the browser cannot draw it: «format not supported» on a photo the person
               just took (prod, Oct 2026). The bytes say what it is; the shared decoder turns it into
               a JPEG once. */
            sniffHeic(file).then(function (heic) {
                if (heic && !(meta && meta.heicTried) && window.HeicDecode && window.HeicDecode.normalize) {
                    var named = new File([file], String(file.name || 'photo').replace(/\.[^.]*$/, '') + '.heic', { type: 'image/heic', lastModified: file.lastModified });
                    return window.HeicDecode.normalize(named, window.__ae || null).then(function (jpg) {
                        stageEvt('heic_as_jpg', { size: file.size });
                        openFile(jpg, Object.assign({}, meta || {}, { heicTried: true }));
                    });
                }
                throw new Error('not_heic');
            }).catch(function () { rejectFile(T.not_supported, 'WRONG_FILE_TYPE'); closeFile(); });
        };
        im.src = url;
        loadExif(file);
    }
}
/* ISO-BMFF «ftyp» with a HEIF brand in the first 12 bytes. */
function sniffHeic(file) {
    try {
        return file.slice(0, 12).arrayBuffer().then(function (b) {
            var u = new Uint8Array(b), s = String.fromCharCode.apply(null, Array.from(u.subarray(4, 12)));
            return /^ftyp(heic|heix|hevc|heim|heis|mif1|msf1)/.test(s);
        }).catch(function () { return false; });
    } catch (_) { return Promise.resolve(false); }
}
function rejectFile(msg, reason) { try { window.__reject(msg, window.ToolErrors && window.ToolErrors.REASONS ? window.ToolErrors.REASONS[reason] : reason); } catch (_) { toast(msg, 'error'); } }
/* The browser could not open the recording: an AVI from a DVR (13 of 14 AVI sessions, Sept–Oct
   2026), MJPEG, MPEG-4 Part 2, an HEVC this machine cannot decode (12 MP4 sessions). The site's one
   ladder — UniversalVideoDecode: as it is → lossless repackage → re-encode, decided by actually
   playing — makes a copy <video> and the frame reader can open, the way /video-blur does. FFmpeg
   loads only here, for the person whose file did not open. The original stays the evidence. */
function loadScriptOnce(src) {
    return new Promise(function (resolve, reject) {
        if (!src) { reject(new Error('no_src')); return; }
        var s = document.createElement('script'); s.src = src; s.async = true;
        s.onload = function () { resolve(); }; s.onerror = function () { reject(new Error('script_failed')); };
        document.head.appendChild(s);
    });
}
function rescueVideo(file, e0) {
    var ext = (file.name.split('.').pop() || '').toLowerCase(), t0 = performance.now();
    stageEvt('decode_rescue_start', { ext: ext, mb: Math.round(file.size / 1048576), why: String(e0 && e0.message || '').slice(0, 40) });
    FX.busy(1, T.rescue_checking);
    var deps = Promise.resolve();
    if ((!window.FFmpegProcessor || window.FFmpegProcessor.__isStub) && typeof window.__ensureFFmpeg === 'function') deps = deps.then(function () { return window.__ensureFFmpeg(); });
    if (!window.UniversalVideoDecode) deps = deps.then(function () { return loadScriptOnce(CFG.universalDecode); });
    deps.then(function () {
        if (!window.UniversalVideoDecode || !window.UniversalVideoDecode.open) throw new Error('decoder_unavailable');
        return window.UniversalVideoDecode.open(file, { onProgress: function (stage) {
            // naming the step buys patience: a repackage is seconds, a re-encode is a full pass
            FX.busy(0, stage === 'transcode' || stage === 'work' ? T.rescue_converting : T.rescue_remuxing);
        } });
    }).then(function (res) {
        FX.busy(-1);
        if (S.file !== file) return;                        // another file was opened meanwhile
        if (res && res.ok && res.converted) {
            var name = res.convertedName || (file.name.replace(/\.[^.]+$/, '') + '.mp4');
            var conv = new File([res.converted], name, { type: res.converted.type || 'video/mp4', lastModified: file.lastModified });
            try { res.cleanup && res.cleanup(); } catch (_) {}
            stageEvt('decode_rescue_ok', { ext: ext, tier: res.tier, ms: Math.round(performance.now() - t0) });
            toast(T.rescue_done, 'info');
            openFile(conv, { rescued: true, original: file, tier: res.tier });
            return;
        }
        stageEvt('decode_rescue_failed', { ext: ext, tier: res && res.tier || '', err: String(res && res.error || '').slice(0, 80) });
        showDecodeFailure(file, e0 && e0.message);
    }).catch(function (err) {
        FX.busy(-1);
        if (S.file !== file) return;
        stageEvt('decode_rescue_failed', { ext: ext, err: String(err && err.message || err).slice(0, 80) });
        showDecodeFailure(file, e0 && e0.message);
    });
}
function showDecodeFailure(file, reason) {
    editor.hidden = true; startEl.hidden = true; decodeFail.hidden = false;
    var ext = (file.name.split('.').pop() || '?').toUpperCase();
    $('fxDecodeExt').textContent = ext;
    stageEvt('decode_failed', { file_ext: ext.toLowerCase(), mime: file.type || 'unknown', reason: reason });
    try { window.__aeDiagnostic && window.__aeDiagnostic({ issue: 'video_decode_failed_' + ext.toLowerCase() }); } catch (_) {}
}
function closeFile(silent) {
    pause();
    FX.reset();
    UI.imgW = UI.imgH = 0; UI.currentCaptured = null; UI.compare = false; $('fxCompare').setAttribute('aria-pressed', 'false');
    if (!silent) { editor.hidden = true; decodeFail.hidden = true; startEl.hidden = false; try { uploader.__uploaderReset && uploader.__uploaderReset(); } catch (_) {} }
}
function updateFileMeta() {
    var parts = [];
    if (S.base) parts.push(S.base.width + '×' + S.base.height);
    if (S.kind === 'video') parts.push('#' + S.sourceMeta.frame + ' · ' + fmtTime(S.sourceMeta.time, true));
    if (S.sourceMeta.stacked) parts.push('Σ' + S.sourceMeta.stacked.n);
    if (S.sourceMeta.tracked) parts.push('⊕' + S.sourceMeta.tracked.used + ' ×' + S.sourceMeta.tracked.K);
    $('fxFileMeta').textContent = parts.join(' · ');
    var kv = [];
    var add = function (k, v) { if (v != null && v !== '') kv.push('<dt>' + esc(k) + '</dt><dd>' + esc(String(v)) + '</dd>'); };
    if (S.file) add(T.info_size, fmtSize(S.file.size));
    if (S.kind === 'video') {
        add(T.info_res, S.video.w + '×' + S.video.h);
        add(T.info_dur, fmtTime(S.video.duration, true));
        add(T.info_fps, S.video.fps + (S.video.fpsExact ? '' : ' (~)'));
        if (S.video.nbFrames) add(T.info_frames, S.video.nbFrames);
        if (S.video.codec) add(T.info_codec, S.video.codec);
        if (S.video.bitrate) add(T.info_bitrate, S.video.bitrate + ' kbit/s');
    } else if (S.source) { add(T.info_res, S.source.width + '×' + S.source.height); add(T.info_type, S.file && S.file.type || '—'); }
    $('fxInfo').innerHTML = kv.join('');
}
FX.on('source', function () { UI.anno.items = UI.anno.items; });
FX.on('base', function () { render(); });
FX.on('current', function () { render(); enableActions(true); });
FX.on('history', function () { $('fxUndo').disabled = !S.history.length; $('fxRedo').disabled = !S.future.length; });
FX.on('reset', function () { ctx.clearRect(0, 0, canvas.width, canvas.height); annoCtx.clearRect(0, 0, annoCanvas.width, annoCanvas.height); emptyEl.hidden = false; enableActions(false); renderStrip(); $('fxUndo').disabled = true; $('fxRedo').disabled = true; });
function enableActions(on) { ['fxDownloadBtn', 'fxCompare', 'fxLoupeBtn'].forEach(function (id) { $(id).disabled = !on; }); }

// Through the shared binder, not a raw listener: a file picked before this
// script finished loading is replayed to us instead of being lost (the race
// UploaderListenerRaceTest exists for).
if (window.__bindUploaderFile) window.__bindUploaderFile(uploader, function (f) { openFile(f); });
else uploader.addEventListener('tool-uploader:file', function (e) { var f = e.detail && e.detail.file; if (f) openFile(f); });
uploader.addEventListener('tool-uploader:clear', function () { closeFile(); });
$('fxOpenNew').addEventListener('click', function () { closeFile(); try { uploader.__uploaderOpen && uploader.__uploaderOpen(); } catch (_) {} });
$('fxDecodeAnother').addEventListener('click', function () { closeFile(); });
$('fxUndo').addEventListener('click', FX.undo);
$('fxRedo').addEventListener('click', FX.redo);

/* ───────────────────────── samples ───────────────────────── */
/* A night dashcam still with a plate, synthesised: the tool can be understood
   without owning a bad recording. Motion blur + sensor noise + JPEG-ish
   softness, in that order, like a real capture. */
function synthFrame(w, h, t, opts) {
    var c = document.createElement('canvas'); c.width = w; c.height = h; var x = c.getContext('2d');
    var sky = x.createLinearGradient(0, 0, 0, h); sky.addColorStop(0, '#05070d'); sky.addColorStop(1, '#141a26'); x.fillStyle = sky; x.fillRect(0, 0, w, h);
    x.fillStyle = '#1c2029'; x.beginPath(); x.moveTo(0, h * 0.62); x.lineTo(w, h * 0.58); x.lineTo(w, h); x.lineTo(0, h); x.fill();
    for (var i = 0; i < 8; i++) { x.fillStyle = 'rgba(255,255,255,.35)'; var lx = w * 0.5 + (i - 4) * 90 - (t * 120 % 90); x.fillRect(lx, h * 0.8, 40, 4); }
    var px = w * 0.42 + Math.sin(t * 2.1) * 6, py = h * 0.36 + Math.cos(t * 1.7) * 4, cw = w * 0.34, ch = h * 0.42;
    x.fillStyle = '#2a2f3a'; x.fillRect(px, py, cw, ch);
    x.fillStyle = '#ff5c3a'; x.fillRect(px + 8, py + ch * 0.55, cw * 0.18, 14); x.fillRect(px + cw - 8 - cw * 0.18, py + ch * 0.55, cw * 0.18, 14);
    x.fillStyle = '#e9ecef'; var pw = cw * 0.46, ph = 22, ppx = px + (cw - pw) / 2, ppy = py + ch * 0.72;
    x.fillRect(ppx, ppy, pw, ph); x.fillStyle = '#0b0d12'; x.font = 'bold 16px Arial, sans-serif'; x.textBaseline = 'middle'; x.textAlign = 'center';
    x.fillText((opts && opts.plate) || 'A 147 KP 77', ppx + pw / 2, ppy + ph / 2 + 1);
    var g = x.createRadialGradient(px + cw * 0.5, py - 20, 10, px + cw * 0.5, py - 20, cw); g.addColorStop(0, 'rgba(255,220,170,.18)'); g.addColorStop(1, 'rgba(0,0,0,0)'); x.fillStyle = g; x.fillRect(0, 0, w, h);
    // motion blur along the road axis
    var blur = document.createElement('canvas'); blur.width = w; blur.height = h; var bx = blur.getContext('2d');
    var k = (opts && opts.blur != null) ? opts.blur : 5;
    bx.globalAlpha = 1 / (k * 2 + 1);
    for (var d = -k; d <= k; d++) bx.drawImage(c, d, Math.round(d * 0.2));
    var id = bx.getImageData(0, 0, w, h), dd = id.data, noise = (opts && opts.noise) || 22;
    for (var p = 0; p < dd.length; p += 4) { var n = (Math.random() - 0.5) * noise * 2; dd[p] = Math.max(0, Math.min(255, dd[p] * 0.55 + n)); dd[p + 1] = Math.max(0, Math.min(255, dd[p + 1] * 0.55 + n)); dd[p + 2] = Math.max(0, Math.min(255, dd[p + 2] * 0.6 + n)); }
    bx.putImageData(id, 0, 0);
    return blur;
}
$('fxSampleImage').addEventListener('click', function () {
    var c = synthFrame(960, 540, 0.3, { blur: 5, noise: 22 });
    c.toBlob(function (blob) { var f = new File([blob], T.sample_name_image + '.jpg', { type: 'image/jpeg' }); openFile(f, { sample: true }); }, 'image/jpeg', 0.6);
});
$('fxSampleVideo').addEventListener('click', function () {
    if (typeof MediaRecorder === 'undefined' || !HTMLCanvasElement.prototype.captureStream) { $('fxSampleImage').click(); return; }
    var btn = this; btn.disabled = true;
    var w = 960, h = 540, fps = 25, frames = 60;
    var mime = ['video/webm;codecs=vp9', 'video/webm;codecs=vp8', 'video/webm', 'video/mp4'].find(function (m) { return MediaRecorder.isTypeSupported(m); });
    // The first video recorder of a Chromium tab drops its first ~2 s of
    // frames — 50 of this 60-frame sample. Warm the encoder at this size.
    (window.__mediaRecorderWarm ? window.__mediaRecorderWarm({ width: w, height: h, mimeType: mime, videoBitsPerSecond: 2500000 }) : Promise.resolve()).then(function () {
    var c = document.createElement('canvas'); c.width = w; c.height = h; var x = c.getContext('2d');
    var stream = c.captureStream(fps);
    var rec = new MediaRecorder(stream, mime ? { mimeType: mime, videoBitsPerSecond: 2500000 } : undefined), chunks = [];
    rec.ondataavailable = function (e) { if (e.data && e.data.size) chunks.push(e.data); };
    rec.onstop = function () {
        btn.disabled = false;
        var blob = new Blob(chunks, { type: rec.mimeType || 'video/webm' });
        openFile(new File([blob], T.sample_name_video + (/mp4/.test(blob.type) ? '.mp4' : '.webm'), { type: blob.type }), { sample: true });
    };
    rec.start();
    var i = 0;
    (function tick() {
        // blur varies frame to frame — the sharpness search has something to find
        var t = i / fps, blur = 2 + Math.round(4 * Math.abs(Math.sin(t * 3.3)));
        x.drawImage(synthFrame(w, h, t, { blur: blur, noise: 16 }), 0, 0);
        if (++i < frames) setTimeout(tick, 1000 / fps); else setTimeout(function () { rec.stop(); }, 120);
    })();
    });
});

/* ───────────────────────── keyboard ───────────────────────── */
document.addEventListener('keydown', function (e) {
    if (window.__keyOwnedByFocus ? window.__keyOwnedByFocus(e) : (e.target.matches('input,textarea,select,[contenteditable]'))) return;
    if (editor.hidden) return;
    var k = e.key;
    var kl = window.__keyLetter ? window.__keyLetter(e) : (/^[a-z0-9]$/i.test(k || '') ? k.toLowerCase() : '');
    var ks = window.__keySign ? window.__keySign(e) : k;
    var mod = e.ctrlKey || e.metaKey || e.altKey;
    if ((e.ctrlKey || e.metaKey) && kl === 'z' && !e.shiftKey) { e.preventDefault(); FX.undo(); return; }
    if ((e.ctrlKey || e.metaKey) && (kl === 'y' || (kl === 'z' && e.shiftKey))) { e.preventDefault(); FX.redo(); return; }
    if ((e.ctrlKey || e.metaKey) && kl === 's') { e.preventDefault(); if (window.FXExport) FXExport.run('png'); return; }
    if (e.code === 'Space') { e.preventDefault(); if (S.kind === 'video') { if (UI.playing) pause(); else play(); } else UI.spaceHeld = true; return; }
    if (k === 'ArrowLeft') { e.preventDefault(); stepFrames(e.shiftKey ? -10 : -1); return; }
    if (k === 'ArrowRight') { e.preventDefault(); stepFrames(e.shiftKey ? 10 : 1); return; }
    if (k === 'Home') { e.preventDefault(); goToTime(0); return; }
    if (k === 'End') { e.preventDefault(); goToTime(S.video.duration - 0.5 / S.video.fps); return; }
    if (ks === '\\') { e.preventDefault(); if (!UI.abHold) { UI.abHold = true; render(); } return; }
    if (k === '+' || k === '=') { zoomAt(1.25); return; }
    if (k === '-') { zoomAt(0.8); return; }
    if (kl === '0' && !mod) { fitView(); return; }
    if (k === 'Escape') { if (UI.compare) { UI.compare = false; $('fxCompare').setAttribute('aria-pressed', 'false'); render(); return; } if (UI.roi) { setRoi(null); return; } if (UI.analysis) { clearAnalysis(); return; } setTool('select'); return; }
    var tabs = { '1': 'frame', '2': 'geom', '3': 'enhance', '4': 'read', '5': 'verify' };
    if (tabs[kl] && !mod) { switchTab(tabs[kl]); return; }
    var tools = { v: 'select', r: 'roi', d: 'ruler', p: 'persp', a: 'anno' };
    if (tools[kl] && !e.ctrlKey && !e.metaKey) { setTool(tools[kl]); return; }
    if (kl === 'l' && !e.ctrlKey && !e.metaKey) { $('fxLoupeBtn').click(); return; }
    if (kl === 'c' && !e.ctrlKey && !e.metaKey) { $('fxCompare').click(); return; }
});
document.addEventListener('keyup', function (e) {
    if (e.code === 'Space') UI.spaceHeld = false;
    if ((window.__keySign ? window.__keySign(e) : e.key) === '\\') { UI.abHold = false; render(); }
});

/* ───────────────────────── init ───────────────────────── */
FX.bindVideo(video);
buildChain();
syncGeomUI();
renderStrip();
try { var savedTab = window.__toolSettings && window.__toolSettings.load(CFG.slug, 'tab', 'frame'); if (savedTab && document.querySelector('.fx-tab[data-tab="' + savedTab + '"]')) switchTab(savedTab); } catch (_) {}
window.FXApp = { UI: UI, render: render, setTool: setTool, switchTab: switchTab, openFile: openFile, closeFile: closeFile, goToTime: goToTime, fitView: fitView, cue: cue, esc: esc, fmtTime: fmtTime, parseTime: parseTime };
})();
